/* --------------------------------- Positionsliste ---------------------------------- */
// Schritt 11 (Konzept § 4.11/§ 2.7, Einheit E, hoechstes Risiko/hoechste Testflaeche): Region 4c
// (`DraggableItemList`) — DndContext, die fuenf Sektionen.
//
// Fachlicher State bleibt bewusst bei `builder.tsx` (Konzept § 3 Tabelle Zeile 1): `patchItem`/
// `removeItem`/`handleDragEnd` schreiben in `items`/`touch()` (Autosave-Kette,
// H-10/H-11) und tragen ihre eigenen H-12-Waechterzeilen dort -- exakt dasselbe Muster wie
// `applyPackage`/`onApply` (Schritt 6) und die sechs Handler aus `VariantenLeiste` (Schritt 9).
// `setPricingMode`/`setMenuTotalPrice` bleiben aus demselben Grund dort (PATCHt `option`, eigenes
// H-12-Gate). `visibleItems`/`ohnePreisIds`/`sektionsSumme`/`sektionsUst`/`guestsForItem`/
// `lmivByCatalogId` sind abgeleiteter State (Konzept § 3 Tabelle Zeile 2) -- mehrere Geschwister
// (Zusammenfassungs-Karte, Schritt 12) brauchen dieselbe Zahl, bleiben daher als `useMemo`/
// `useCallback` beim Parent. `bearbeiteteAbschnitte`/`abschnittBearbeiten` UND `openSection`/
// `setOpenSection` sehen wie rein lokaler UI-State aus, haben aber je eine EXTERNE Schreibquelle
// (`openPositionDrawer()` schaltet `bearbeiteteAbschnitte` von aussen — auch aus `VariantenLeiste`
// erreichbar; `loadFull()` setzt `openSection` beim ersten Laden) -- derselbe Fall wie `activeDay`/
// `offenerReiter` bei `NachschlagenTabs` (Schritt 10), bleiben daher beim Parent.
//
// Phase B (Konzept § 5): `bearbeiteteZeilen` + ihr Ref-Begleiter `zeilenZustandRef` (der
// „Abbrechen"-Rueckhalt, § 6.2: State und Ref-Begleiter wandern gemeinsam, nie einzeln) sind
// vollstaendig HIER hineingewandert -- keine externe Schreibquelle (anders als
// `bearbeiteteAbschnitte`), betreffen ausschliesslich diese Komponente (Konzept § 3 Tabelle
// Zeile 3). `zeileAbbrechen` ruft weiterhin `patchItem` (Prop) -- derselbe Weg zum Server wie
// jede andere Aenderung, kein Sonderweg. `dndSensors` (PointerSensor/KeyboardSensor) ist reine,
// zustandslose dnd-kit-Verkabelung ohne anderen Konsumenten -- ebenfalls HIER hineingewandert,
// direkt neben dem einzigen `DndContext`, der sie liest.
import { Fragment, useId, useRef, useState, type ReactNode } from "react";
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { lineGross, alsSektionsModus, istPauschal as istPauschalerModus, type PauschalTeil, type SektionsPreisModus } from "@maestro/pricing";
import type { CatalogItem, OfferItem, OfferOption, OfferSection } from "./types";
import { centsToEuros, money, parseGermanEurosToCents } from "./format";
import { Button, Card, Icon, Input } from "./components";
import { ItemRow } from "./ItemRow";
import { RASTER_43_SPALTEN } from "./builder-raster";
import { aggregateAllergens, lmivLabel } from "./lmiv";
import { SECTIONS } from "../pages/builder";
import { gaesteOhnePaket, toEngineItems, uuid } from "../lib/builder-helpers";

/** Finger-Drag erst nach 250 ms Halten; wer vorher mehr als 8 px wischt, scrollt (Antoine 16.10.2026). */
export const DND_TOUCH_HALTEN = { delay: 250, tolerance: 8 };

/** B-4: die Felder, die das Zeilenformular aendern kann — und damit genau die, die „Abbrechen"
 *  zuruecksetzt. Bewusst eine Auswahl, kein Abbild der ganzen Zeile (siehe `zeilenZustandRef`). */
type ZeilenStand = Pick<OfferItem, "name" | "qtyMilli" | "unitPriceCents" | "priceMode" | "vatRateBp">;

/**
 * Migration 384 (Wahlgruppe): fasst KONSEKUTIVE Positionen mit derselben `choiceGroupId` zu
 * einem Render-Block zusammen — die Sortier-Reihenfolge (drag & drop, `sortOrder`) bleibt die
 * einzige Quelle, es gibt keine zweite Gruppierungs-Sortierung. Eine Gruppe mit nur noch EINEM
 * verbliebenen Mitglied (Rest z. B. geloescht) wird wie eine normale Einzelzeile behandelt —
 * `choiceGroupId` bleibt auf der Zeile gesetzt, bis sie explizit geloest wird, aber ein Rahmen
 * um eine einzelne Zeile waere irrefuehrend ("Wahlmenü" mit nur einer Option).
 */
type RenderBlock = { kind: "single"; item: OfferItem } | { kind: "gruppe"; groupId: string; items: OfferItem[] };
function gruppiereWahlgruppen(items: OfferItem[]): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  for (const it of items) {
    const letzter = blocks[blocks.length - 1];
    if (it.choiceGroupId != null && letzter?.kind === "gruppe" && letzter.groupId === it.choiceGroupId) {
      letzter.items.push(it);
    } else if (it.choiceGroupId != null) {
      blocks.push({ kind: "gruppe", groupId: it.choiceGroupId, items: [it] });
    } else {
      blocks.push({ kind: "single", item: it });
    }
  }
  return blocks.map((b) => (b.kind === "gruppe" && b.items.length < 2 ? { kind: "single" as const, item: b.items[0]! } : b));
}

/**
 * Punkt 52 (Antoine 18.09.2026): „Ich möchte hier pro Gang eine Überschrift eingeben können
 * (nicht muss) und zwischen zwei Gängen ein und/oder eingeben können (keine Pflicht)."
 *
 * Fasst KONSEKUTIVE Positionen mit derselben `courseLabel` zu einem Gang-Block zusammen — genau
 * wie `gruppiereWahlgruppen` arbeitet das ausschliesslich auf der bestehenden Sortier-Reihenfolge
 * und legt keine zweite Ordnung an. Die Ueberschrift ist eine Eigenschaft der ZEILEN (das Feld
 * `course_label` gibt es seit sql/105, die Kundenseite liest es bereits), sichtbar und bedienbar
 * aber als EINE Ueberschrift ueber dem Block — Antoine will eine Eingabe je Gang, nicht eine je
 * Zeile. Das Setzen patcht deshalb alle Zeilen des Blocks.
 *
 * Positionen ohne Ueberschrift (`courseLabel == null`) bilden ebenfalls einen Block, nur ohne
 * sichtbare Zeile darueber. Sonst waere die erste Position eines Angebots nicht mit einer
 * Ueberschrift versehbar, ohne vorher irgendwo anders zu klicken.
 */
/**
 * Punkt 60: steht zwischen diesen beiden Positionen ein „oder"? Genau dann, wenn beide dieselbe
 * Wahlgruppe tragen. `null` auf beiden Seiten ist KEINE gemeinsame Gruppe — zwei Zeilen ohne
 * Wahlgruppe sind zwei Gerichte, die beide serviert werden.
 */

export function verbindungOder(zeile: OfferItem, davor: OfferItem | null): boolean {
  if (!davor) return false;
  return zeile.choiceGroupId != null && zeile.choiceGroupId === davor.choiceGroupId;
}

/** Die Position, die in der Sektionsliste unmittelbar vor dieser steht. */
export function vorherigeZeile(alle: OfferItem[], zeile: OfferItem): OfferItem | null {
  const i = alle.findIndex((x) => x.id === zeile.id);
  return i > 0 ? alle[i - 1]! : null;
}

/**
 * Punkt 60: was beim Klick auf den Trenner geschieht — und zwar für BEIDE Zeilen, nicht nur für
 * die untere. Zurückgegeben werden die Änderungen, die zu schreiben sind; das Schreiben selbst
 * bleibt beim Aufrufer, damit diese Entscheidung ohne Oberfläche prüfbar ist.
 *
 * Aus „und" wird „oder": beide Zeilen bekommen dieselbe Gruppe. Hängt die obere schon in einer
 * Gruppe, wird DEREN Kennung übernommen — sonst zerfiele eine bestehende Dreier-Auswahl in zwei
 * Paare. Trägt nur die untere eine Gruppe, gilt dasselbe umgekehrt.
 *
 * Aus „oder" wird „und": nur die UNTERE Zeile verlässt die Gruppe. Die obere bleibt, wo sie ist —
 * bei „Vorspeise A oder B oder C" trennt ein Klick zwischen B und C genau dort, statt die ganze
 * Auswahl aufzulösen.
 */
export function verbindungsAenderung(
  zeile: OfferItem,
  davor: OfferItem | null,
  neueGruppeId: () => string,
): { id: string; choiceGroupId: string | null }[] {
  if (!davor) return [];
  if (verbindungOder(zeile, davor)) return [{ id: zeile.id, choiceGroupId: null }];
  const gruppe = davor.choiceGroupId ?? zeile.choiceGroupId ?? neueGruppeId();
  const aenderungen: { id: string; choiceGroupId: string | null }[] = [];
  if (davor.choiceGroupId !== gruppe) aenderungen.push({ id: davor.id, choiceGroupId: gruppe });
  if (zeile.choiceGroupId !== gruppe) aenderungen.push({ id: zeile.id, choiceGroupId: gruppe });
  return aenderungen;
}

type GangBlock = { schluessel: string; label: string | null; items: OfferItem[] };
export function gruppiereGaenge(items: OfferItem[]): GangBlock[] {
  const blocks: GangBlock[] = [];
  for (const it of items) {
    const label = it.courseLabel?.trim() ? it.courseLabel.trim() : null;
    const letzter = blocks[blocks.length - 1];
    if (letzter && letzter.label === label) {
      letzter.items.push(it);
    } else {
      blocks.push({ schluessel: `${label ?? "ohne"}-${it.id}`, label, items: [it] });
    }
  }
  return blocks;
}

/** Vorschlaege aus Antoines Auftrag — frei ueberschreibbar, keine Auswahlliste. */
const GANG_VORSCHLAEGE = ["Vorspeise", "Zwischengang", "Hauptspeise", "Dessert"];

/**
 * Punkt 75, Schritt 3 — die Gestaltungswerte aus Entwurf 41a, an EINER Stelle.
 *
 * Antoine am 20.09.2026, nachdem Schritt 2 nur die Struktur übernommen hatte: „In 41a gibt es doch
 * verschiedene Farben, Überschriften etc. Davon hast du nichts übernommen. Ich möchte dass das
 * Design zu 100%, wo möglich, übernommen und umgesetzt wird." Und unmissverständlich hinterher:
 * „Übernimm keine NEUE Funktionen. Es geht mir NUR UMS DESIGN … **ES GEHT NUR UMS DESIGN!!!**"
 *
 * Die Werte unten sind aus seiner Entwurfsdatei GEZOGEN, nicht geschätzt.
 *
 * ── Warum feste Werte und nicht die Gestaltungsmarken des Hauses ────────────────────────────
 * Der Entwurf bringt eine eigene warme Palette mit (cremefarbener Grund, dunkelbraune Schrift,
 * Rotbraun als Akzent), die es im Haus so nicht gibt. Die HAUSMARKEN zu ändern hieße, jede andere
 * Seite mitzuverändern — das wäre weit mehr als der Auftrag. Also stehen die Werte hier, an der
 * Stelle, für die sie gelten, und nur dort.
 *
 * ── Warum die hellen Werte fest sind und die dunklen Marken bleiben ─────────────────────────
 * Der Entwurf kennt nur den hellen Modus. Ersetzte man die Marken ersatzlos, wäre die Liste im
 * dunklen Modus unlesbar — weiße Karten auf dunklem Grund. Jede Zeile trägt deshalb BEIDES: den
 * festen Wert aus dem Entwurf und die `dark:`-Marke, die bisher dort stand.
 *
 * ── Was NICHT übernommen wird ───────────────────────────────────────────────────────────────
 * Der Entwurf zeigt auch Amber-Chips („Preis fehlt", „2 Preise fehlen"), die Fußzeile „Ohne Preis
 * zählt die Sektion nicht in die Angebotssumme", die Aktion „Preise aus Katalog übernehmen ✨",
 * ein Duplizieren-Icon und den Hinweis „≠ 18 Gäste". **Das sind Funktionen, keine Gestaltung** —
 * und Antoine hat sie zweimal ausdrücklich ausgeschlossen. Sie bleiben draußen.
 */
/**
 * ENTWURF 43a — DIE ERGÄNZUNGEN ZUM AUSSEHEN.
 *
 * Antoine am 22.09.2026: **„Es geht nur darum, dass der Angebotsbilder im Moment fürchterlich
 * ausschaut und nicht so wie auf dem Bild. Ich möchte, dass er so ausschaut wie in Design 43."**
 * Und ausdrücklich: **„Bitte keine neuen Funktionalitäten. … Das Design ist nicht dafür da neue
 * Funktionalitäten in zuzufügen oder andere wegzulassen."**
 *
 * Der Entwurf nennt vier Missstände am heutigen Stand:
 *   1. „und" und „oder" sehen identisch aus, obwohl sie Gegenteiliges bedeuten.
 *   2. Die Chips stehen als eigene Zeilen zwischen allen Positionen und kosten je ~50 px.
 *   3. Das Ketten-Icon steht ohne Erklärung dauerhaft in jeder Zeile.
 *   4. Gang-Überschriften sind normale Zeilen ohne Zähler oder Zwischensumme.
 * (Punkt 4 war mit Entwurf 41a bereits behoben — Zähler und Zwischensumme stehen dort schon.)
 *
 * Die Maße stammen unverändert aus der Vorlage: Wahlgruppen-Karte 11 px Radius mit 12/18 px
 * Rand, Trennlinie #EFE8DA, Chips 18 px hoch mit 10,5 px Schrift. Die Palette teilt sich diese
 * Tabelle mit `E41A` darüber — zwei Paletten wären zwei Wahrheiten über dieselbe Fläche.
 */
const E43 = {
  /** Trenner: 6 px Polsterung statt einer ~50 px hohen Zeile; die Linie läuft durch. */
  trennerZeile: "flex items-center gap-[10px] px-[18px] py-[6px]",
  /** Platzhalter in der Breite der Griffspalte — der Chip beginnt bündig mit den Positionen. */
  trennerGriffSpalte: "w-[30px] shrink-0",
  trennerLinie: "h-px flex-1 bg-[#EFE8DA] dark:bg-outline/25",
  /** „und": neutral. Es ist der Normalfall und soll nicht um Aufmerksamkeit bitten. */
  chipUnd:
    "shrink-0 rounded-[5px] border border-transparent px-[6px] text-[11px] text-[#9A9080] transition-colors hover:border-[#E3DBCB] hover:bg-[#FBF8F1] hover:text-[#57503F] dark:text-secondary-fixed-dim dark:hover:border-outline/40 dark:hover:bg-surface-variant/20",
  /** „oder": Akzentfarbe. Es bedeutet eine Wahl des Gastes und muss sich auf einen Blick von „und" unterscheiden. */
  chipOder:
    "shrink-0 rounded-[5px] border border-[#E4C7B4] bg-[#FDF3EC] px-[6px] text-[11px] font-semibold text-[#C2410C] transition-colors hover:bg-[#F9E6DA] dark:border-primary/40 dark:bg-primary/10 dark:text-primary-fixed-dim",
  /** Dasselbe in der Leseansicht — ohne Knopf-Verhalten. */
  chipOderStatisch:
    "shrink-0 rounded-[5px] border border-[#E4C7B4] bg-[#FDF3EC] px-[6px] text-[11px] font-semibold text-[#C2410C] dark:border-primary/40 dark:bg-primary/10 dark:text-primary-fixed-dim",
  /** Die Wahlgruppe bekommt eine eigene, eingerückte Karte — sie ist EIN Gang mit Alternativen. */
  /*
   * Die letzte Zeile IN der Karte zieht sonst ihre eigene Trennlinie direkt über den unteren
   * Rahmen — zwei Linien im Abstand von einem Pixel. Derselbe Befund („hier sind ueberall
   * doppelte Linien drin") wie am 20.09.2026, nur eine Ebene tiefer.
   */
  wahlKarte:
    "mx-[18px] my-[12px] overflow-visible rounded-[11px] border border-[#EFE8DA] bg-white [&>*:last-child]:border-b-0 dark:border-outline/30 dark:bg-surface-variant/5",
  /*
   * Eine Kopfzeile „Gast wählt 1 von 2" stand hier als Entwurf — die RENDERTE Vorlage hat
   * keine: dort sagen die Radio-Ringe und die „oder"-Marken alles, was die Karte bedeutet, und
   * der Gang darüber trägt bereits den Chip „Wahlgang" mitsamt Erklärung. Eine zusätzliche
   * Zeile hätte dasselbe ein drittes Mal behauptet und jede Wahlgruppe um 30 px verlängert.
   */
  /** Gestrichelte Aktion am Ende eines Gangs. */
  gangAktion:
    "mx-[18px] my-[8px] flex w-[calc(100%-36px)] items-center justify-center gap-[6px] rounded-[8px] border border-dashed border-[#E3DBCB] px-[10px] py-[7px] text-[11.5px] text-[#9A9080] transition-colors hover:border-[#C9BFAD] hover:bg-[#FBF8F1] hover:text-[#57503F] dark:border-outline/35 dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/15",
  /**
   * Mockup v3: die Einfügelinie zwischen zwei Gängen — „+ Position" / „+ Gang" im Zuschnitt des
   * „und"-Chips, sichtbar beim Darüberfahren und bei Tastaturfokus. Ohne Zeiger (Touch) bleiben
   * „+ Position" im Abschnittskopf und „Gang hinzufügen" am Ende.
   */
  chipEinfuegen:
    "inline-flex shrink-0 items-center gap-[3px] rounded-[5px] border border-[#E3DBCB] bg-white px-[6px] text-[11px] text-[#57503F] transition-opacity hover:bg-[#FBF8F1] focus-visible:opacity-100 dark:border-outline/40 dark:bg-night-card dark:text-secondary-fixed-dim [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/trenner:opacity-100 [@media(hover:none)]:hidden",
  /** Die Gang-Nummer vor der Überschrift. */
  gangNummer:
    "shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#9A9080] dark:text-secondary-fixed-dim",
} as const;

/**
 * Der Text der Legende — wortgleich aus Entwurf 43 übernommen.
 *
 * Er steht als Konstante, weil er an genau EINER Stelle die Bedeutung dreier Zeichen festlegt.
 * Stünde er inline, wäre er beim nächsten Abschnitt abgeschrieben und beim übernächsten
 * abgewandelt — und dann erklärte die Liste sich selbst auf drei verschiedene Weisen.
 */
const LEGENDE_43 =
  "„oder“ = Alternativen eines Gangs, der Gast wählt eine · „und“ = nächster Gang · " +
  "Kette löst eine Position aus der Wahlgruppe";

const E41A = {
  /** Karte: weiß, feiner Rand, 14 px Radius, weicher tiefer Schatten. */
  karte: "rounded-[14px] border border-[#EFE8DA] bg-white shadow-[0_8px_18px_-10px_rgba(45,38,26,.14)] dark:border-outline/30 dark:bg-night-card dark:shadow-none",
  /** Sektionskopf: ein Hauch wärmer als die Karte. */
  sektionsKopf: "border-b border-[#EFE8DA] bg-[#FFFDF8] px-[18px] py-[13px] dark:border-outline/30 dark:bg-surface-variant/10",
  /** Tabellenkopf: gedämpfter Grund, Versalien, weite Laufweite. */
  tabellenKopf:
    "border-b border-[#EFE8DA] bg-[#FBF8F1] px-[18px] py-[7px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#9A9080] dark:border-outline/30 dark:bg-surface-variant/15 dark:text-secondary-fixed-dim",
  /** Gangzeile: wie der Sektionskopf, aber mit der leichteren Trennlinie der Zeilen. */
  gangZeile: "border-b border-[#F6F0E4] bg-[#FFFDF8] px-[18px] py-[9px] dark:border-outline/20 dark:bg-surface-variant/10",
  /** Positionszeile: reines Weiß, beim Darüberfahren der wärmste Ton der Palette. */
  positionsZeile: "border-b border-[#F6F0E4] bg-white hover:bg-[#FBF6EC] dark:border-outline/20 dark:bg-transparent dark:hover:bg-surface-variant/15",
  /** Fußzeile je Abschnitt. */
  fussZeile: "bg-[#FBF8F1] px-[18px] py-[10px] text-[12px] text-[#6F675A] dark:bg-surface-variant/15 dark:text-secondary-fixed-dim",
  // `raster` stand hier bis zum 22.09.2026 — ein ZWEITES Raster neben dem der Zeilen, das
  // diesen nur ähnlich sah. Es gibt jetzt genau eines, in `builder-raster.ts`.
  /** Chip, neutral: Positionszähler, Allergen-Buchstaben. */
  chip: "inline-flex h-[18px] items-center rounded-[5px] bg-[#F0EAE0] px-[6px] text-[10.5px] font-semibold text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  /** Chip, Wahlgang: derselbe Zuschnitt, andere Farbe. */
  chipWahl: "inline-flex h-[18px] items-center rounded-[5px] bg-[#F0EAE0] px-[6px] text-[10.5px] font-semibold text-[#6F675A] dark:bg-primary/15 dark:text-primary-fixed-dim",
  /** Der Knopf im Sektionskopf, in der Akzentfarbe des Entwurfs. */
  knopfAkzent:
    "inline-flex h-[28px] items-center gap-[5px] rounded-[8px] bg-[#C2410C] px-[11px] text-[12px] font-semibold text-white transition-colors hover:bg-[#A8380B]",
  /** Der Chip, der das Preismodell nennt. */
  chipModell:
    "inline-flex h-[28px] items-center gap-[5px] rounded-[8px] border border-[#E3DBCB] bg-white px-[10px] text-[12px] font-medium text-[#57503F] transition-colors hover:border-[#C9BFAD] dark:border-outline/40 dark:bg-night-card dark:text-inverse-on-surface",
} as const;

/**
 * Punkt 60 (Antoine 19.09.2026, mit Bildschirmfoto): „Ich sehe übrigens pro Gang ein und aber kein
 * Oder. Wie kann ich aus dem und ein Oder machen?" und, eindeutig: „Das und müsste auswählbar sein
 * in ein Oder."
 *
 * Er hatte recht, und es war eine echte Lücke: eine Wahlgruppe konnte in der Maske GELÖST, aber nie
 * ANGELEGT werden. Nur der Assistent erzeugte sie (Migration 384). Wer drei Vorspeisen zur Auswahl
 * stellen wollte, hatte keinen Weg dorthin.
 *
 * Das „und" zwischen zwei Positionen ist deshalb ein Schalter geworden. „und" heißt: beides wird
 * serviert. „oder" heißt: der Gast wählt eines davon — technisch dieselbe `choiceGroupId` auf
 * beiden Zeilen.
 *
 * Antoines Beispiel: „drei Vorspeisen zur Auswahl … dann ein Hauptgericht und dort gibt es wieder
 * drei Hauptgerichte zum Wählen und beispielsweise nur ein Dessert." Genau das entsteht, wenn er
 * innerhalb der Vorspeisen zweimal „oder" klickt und zwischen den Gängen „und" stehen lässt.
 *
 * ── Punkt 75, Schritt 2 (Entwurf 41a, Befund ①) ─────────────────────────────────────────────
 * Der Entwurf zählt sieben „und"-Trennzeilen, die „keine Information tragen, aber ~50 px je Stück
 * kosten" — und ersetzt sie ersatzlos. Ersatzlos geht hier nicht: Antoines Abgrenzung lautet
 * „keine bisherigen Funktionen weglassen", und dieser Schalter ist die EINZIGE Stelle, an der
 * eine Wahlgruppe überhaupt angelegt werden kann (Punkt 60, und vorher war genau das die Lücke).
 *
 * Der Befund stimmt trotzdem — aber nur für die Hälfte der Fälle. **„und" ist der Normalfall und
 * sagt nichts; „oder" ist die Ausnahme und sagt alles.** Also bekommt jeder Zustand den Platz,
 * den er verdient:
 *
 *   * **„oder"** bleibt unverändert sichtbar — ein Chip in der Akzentfarbe, der ins Auge fällt.
 *   * **„und"** schrumpft auf eine feine Trennlinie. Der Knopf sitzt darauf und wird sichtbar,
 *     sobald die Maus in die Nähe kommt **oder er den Tastaturfokus bekommt**.
 *
 * Das Zweite ist der Punkt, an dem so eine Verdichtung sonst zur Falle wird: ein Knopf, der nur
 * bei `hover` erscheint, ist auf einem Berührungsbildschirm und für die Tastatur verschwunden.
 * Er bleibt deshalb im Tab-Fluss, behält seine Beschriftung für Screenreader, wird bei `focus`
 * voll sichtbar — und unterhalb der Tablet-Breite, wo es kein Darüberfahren gibt, steht er
 * dauerhaft da wie bisher.
 */
function VerbindungsSchalter({
  oder,
  editable,
  onUmschalten,
  einfuegen,
}: {
  oder: boolean;
  editable: boolean;
  onUmschalten: () => void;
  /** Mockup v3: zwischen zwei Gängen sitzen „+ Position"/„+ Gang" auf derselben Linie. */
  einfuegen?: ReactNode;
}) {
  /*
   * ENTWURF 43a — WARUM DIESE ZEILE KEINE ZEILE MEHR IST.
   *
   * Der Entwurf benennt den Missstand selbst: **„die Chips stehen als eigene Zeilen zwischen
   * allen Positionen und kosten je ~50 px"** — und: **„‚und' und ‚oder' sehen identisch aus,
   * obwohl sie Gegenteiliges bedeuten (neuer Gang vs. Alternative im selben Gang)."** Bei einem
   * Menü mit zwölf Positionen gingen so über 500 Pixel für Trennzeilen drauf, und die Liste las
   * sich wie ein Formular statt wie eine Speisekarte.
   *
   * Jetzt: **„und" ist ein neutraler Chip auf einer Hairline** — die Linie läuft durch, der Chip
   * sitzt darauf, die Zeile schrumpft von ~50 px auf 6 px Polsterung. **„oder" ist ein Chip in
   * der Akzentfarbe auf der Verbindungslinie** innerhalb der Wahlgruppen-Karte. Zwei
   * unverwechselbare Zustände, gleiche Bedienung.
   *
   * WAS UNVERÄNDERT BLEIBT: der Klick wechselt wie bisher (`verbindungsAenderung`, Punkt 60),
   * und **beide Zustände bleiben dauerhaft sichtbar** — Antoine am 20.09.2026: „das und/oder muss
   * jetzt permanent eingearbeitet werden. Was ist noch alles verschwunden? NICHTS DARF WEG SEIN."
   */
  if (!editable) {
    // Die Leseansicht zeigt „oder" weiterhin — es beschreibt, was der Gast bekommt. „und" ist
    // dort schlicht die Voreinstellung und braucht keine eigene Zeile.
    return oder ? (
      <div className={E43.trennerZeile}>
        <span className={E43.trennerGriffSpalte} />
        <span className={E43.trennerLinie} />
        <span className={E43.chipOderStatisch}>oder</span>
        <span className={E43.trennerLinie} />
      </div>
    ) : null;
  }
  return (
    <div className={`group/trenner ${E43.trennerZeile}`} data-testid={oder ? "verbindung-oder" : "verbindung-und"}>
      <span className={E43.trennerGriffSpalte} />
      <span className={E43.trennerLinie} />
      <button
        type="button"
        onClick={onUmschalten}
        aria-label={oder ? "„oder“ — der Gast wählt eines. Klicken für „und“" : "„und“ — beides wird serviert. Klicken für „oder“"}
        title={oder ? "Der Gast wählt eine dieser Positionen. Klicken macht daraus zwei Gänge." : "Beides wird serviert. Klicken macht daraus eine Auswahl."}
        className={oder ? E43.chipOder : E43.chipUnd}
      >
        {oder ? "oder" : "und"}
      </button>
      {einfuegen}
      <span className={E43.trennerLinie} />
    </div>
  );
}

/**
 * Die Ueberschrift eines Gang-Blocks: gesetzt wird sie als fette Zeile mit Stift gezeigt, fehlt
 * sie, steht dort nur ein zurueckhaltender Knopf. Bewusst kein Dauer-Eingabefeld — in einem
 * Angebot mit acht Positionen waeren das acht leere Felder ueber einer ohnehin dichten Liste.
 */
function GangUeberschrift({
  label,
  editable,
  onSetzen,
  anzahl,
  nummer,
  summeCents,
  istWahlgang,
  nurZubuchbar = false,
}: {
  label: string | null;
  editable: boolean;
  onSetzen: (wert: string | null) => void;
  /** Punkt 75: wie viele Positionen der Gang traegt. */
  anzahl: number;
  /**
   * Entwurf 43a: die laufende Nummer des Gangs („Gang 1 · Antipasto").
   *
   * Sie steht NUR vor einem benannten Gang. Positionen ohne Überschrift bilden im Modell zwar
   * ebenfalls einen Block, sind aber kein Gang im Sinne der Speisekarte — eine Nummer davor
   * behauptete eine Gliederung, die niemand angelegt hat.
   */
  nummer: number | null;
  /** Punkt 75: Zwischensumme des Gangs in Cent — dieselbe Rechnung wie die Abschnittssumme.
   *  `null` (Mockup v3): alles im Paketpreis — dann steht „im Paket" statt „0,00 €". */
  summeCents: number | null;
  /** Punkt 75: tragen ALLE Positionen des Gangs dieselbe Wahlgruppe? Dann waehlt der Gast eine. */
  istWahlgang: boolean;
  /** Befund 14.10.2026: nur zubuchbare Positionen — der Gang zaehlt nicht in die Summe. */
  nurZubuchbar?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState(label ?? "");
  const listeId = useId();

  /*
   * Punkt 75 (Entwurf 41a, Befund ⑧ „weder Gang-Zwischensummen noch Preis pro Person"):
   * Rechts in der Gruppenzeile stehen jetzt Positionszaehler und Zwischensumme des Gangs. Beides
   * ist reine ANZEIGE aus Zahlen, die ohnehin schon gerechnet werden — keine neue Faehigkeit,
   * und damit innerhalb von Antoines Abgrenzung („nur das Design umsetzen").
   */
  const rechts = (
    <span className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-x-3">
      {/* Entwurf 41a: Zaehler 11,5 px in der leisesten Farbe, Summe 12 px/600 eine Stufe kraeftiger
          — die Zahl traegt die Aussage, der Zaehler erlaeutert sie. */}
      <span className="text-[11.5px] text-[#9A9080] dark:text-secondary-fixed-dim">
        {anzahl} {anzahl === 1 ? "Position" : "Positionen"}
        {istWahlgang ? " · Wahl" : ""}
      </span>
      <span className="text-[12px] font-semibold tabular-nums text-[#6F675A] dark:text-inverse-on-surface">{summeCents == null ? "im Paket" : nurZubuchbar ? "zubuchbar" : money(summeCents)}</span>
    </span>
  );

  if (!editable) {
    return label ? (
      <div className={`flex flex-wrap items-center gap-2 text-[12.5px] font-semibold text-[#57503F] dark:text-inverse-on-surface ${E41A.gangZeile}`}>
        {nummer != null && <span className={E43.gangNummer}>Gang {nummer}</span>}
        {label}
        {istWahlgang && <WahlgangChip />}
        {rechts}
      </div>
    ) : null;
  }

  if (offen) {
    const speichern = () => {
      const roh = entwurf.trim();
      onSetzen(roh === "" ? null : roh);
      setOffen(false);
    };
    return (
      <div className={`flex items-center gap-1.5 ${E41A.gangZeile}`}>
        <input
          autoFocus
          aria-label="Gang-Überschrift"
          list={listeId}
          value={entwurf}
          onChange={(e) => setEntwurf(e.target.value)}
          onBlur={speichern}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              speichern();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setEntwurf(label ?? "");
              setOffen(false);
            }
          }}
          placeholder="z. B. Vorspeise"
          className="w-48 rounded border border-outline-variant/60 bg-surface px-2 py-1 text-beleg-klein font-semibold text-on-surface dark:border-outline/40 dark:bg-surface-variant/10 dark:text-inverse-on-surface"
        />
        <datalist id={listeId}>
          {GANG_VORSCHLAEGE.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${E41A.gangZeile}`}>
      <button
        type="button"
        data-testid="gang-ueberschrift-setzen"
        onClick={() => {
          setEntwurf(label ?? "");
          setOffen(true);
        }}
        className={
          label
            ? "group flex items-center gap-1 text-[12.5px] font-semibold text-[#57503F] dark:text-inverse-on-surface"
            : "flex items-center gap-1 text-[11.5px] text-[#9A9080] hover:text-[#57503F] dark:text-secondary-fixed-dim dark:hover:text-on-surface"
        }
      >
        {label ? (
          <>
            {nummer != null && <span className={`${E43.gangNummer} mr-1`}>Gang {nummer}</span>}
            {label}
            <Icon name="edit" className="text-[13px] opacity-0 transition-opacity group-hover:opacity-70" />
          </>
        ) : (
          <>
            <Icon name="add" className="text-[13px]" />
            Gang-Überschrift
          </>
        )}
      </button>
      {istWahlgang && <WahlgangChip />}
      {label != null && rechts}
    </div>
  );
}

/**
 * Punkt 75 (Entwurf 41a, Befund ①): Der Entwurf ersetzt die „und"-Trennzeilen durch einen Chip am
 * Gang — sieben Zeilen à ~50 px, die nichts sagten, was die Gang-Überschrift nicht schon sagt.
 *
 * Dieser Chip ist ausdrücklich NUR ANZEIGE. Das Umschalten zwischen „und" und „oder" bleibt dort,
 * wo es heute ist (`VerbindungsSchalter` zwischen den Zeilen) — es ist eine bestehende Funktion,
 * und Antoines Abgrenzung lautet „keine bisherigen Funktionen weglassen". Der Chip sagt dem
 * Betreiber auf einen Blick, DASS dieser Gang eine Wahl ist; wie er sie ändert, steht unverändert
 * zwischen den Positionen.
 */
function WahlgangChip() {
  return (
    <span className={`shrink-0 ${E41A.chipWahl}`} title="Der Gast wählt eine dieser Positionen">
      Wahlgang
    </span>
  );
}

/**
 * Bughunt 27.08.2026, ERWEITERT 09.09.2026 (Antoine, Screenshot Menü vs. Equipment: "Equipment
 * sollte es auch Pro Person/Gesamtpreis sein, nicht pro Zeile — und im selben Layout wie bei
 * Menü. Es soll überall gleich sein."): welche Sektionen bekommen den Preismodell-Umschalter
 * (Preis pro Gang/Position <-> Gesamtpreis)? Bewusst eine explizite Liste statt "alle" — nur
 * wegen der Wortwahl unten ("Menüpreis"/"Getränkepreis"/...), die ohnehin einen eigenen Eintrag
 * je Sektion braucht, NICHT weil der Mechanismus selbst auf Menü/Getraenke beschraenkt waere:
 * `packages/pricing` (`sectionPricing`), die Route `POST .../offers/:id/section-pricing`
 * (Zod-Enum `["drinks","equipment","staff","other"]`) und `ungedeckteNullpreisIds` sind bereits
 * seit der Verallgemeinerung vollstaendig sektionsgenerisch — Equipment/Personal/Sonstiges waren
 * bereits gebaut und getestet, standen nur mangels UI-Eintrag hier ohne den Umschalter da (die
 * fruehere Fassung dieses Kommentars nannte das noch bewusst ausgenommen, weil Antoines Anlass
 * damals konkret nur die Getraenkepauschale war). Equipment jetzt ergaenzt (der konkrete Auftrag);
 * Personal/Sonstiges gleich mit (dieselbe Luecke, derselbe Mechanismus, Antoines "überall gleich"
 * woertlich genommen) — kein Aufwand ausser dem Wortlaut-Eintrag, kein neuer Code-Pfad.
 */
const GESAMTPREIS_SEKTIONEN: Partial<Record<OfferSection, { preiswort: string; praepositional: string; proEinheit: string }>> = {
  menu: { preiswort: "Menüpreis", praepositional: "fürs Menü", proEinheit: "Preis pro Gang" },
  drinks: { preiswort: "Getränkepreis", praepositional: "für Getränke", proEinheit: "Preis pro Position" },
  equipment: { preiswort: "Equipmentpreis", praepositional: "fürs Equipment", proEinheit: "Preis pro Position" },
  staff: { preiswort: "Personalpreis", praepositional: "fürs Personal", proEinheit: "Preis pro Position" },
  other: { preiswort: "Pauschalpreis", praepositional: "für Sonstiges", proEinheit: "Preis pro Position" },
};

/**
 * WELCHE SEKTIONEN KENNEN EINEN PREIS PRO PERSON — und welche nicht.
 *
 * Antoine, 17.09.2026, nach den Rückmeldungen des Betreibers: „Ein Gesamtpreis sollte außer bei
 * Menü und Getränken auch nicht pro Person geteilt werden."
 *
 * Das trifft die Sache: Essen und Trinken fallen je Gast an, eine Raummiete, das Personal und das
 * Equipment nicht. Wer bei „Sonstiges" 6.500 € einträgt, meint 6.500 € — nicht 6.500 € mal
 * Gästezahl. Ein Knopf, der bei diesen Sektionen „pro Person" bedeutet, lädt genau zu dem Fehler
 * ein, der am 17.09.2026 gemeldet wurde (aus 6.500 € wurden 78.000 €).
 *
 * Deshalb bekommen `equipment`/`staff`/`other` nur EINEN Pauschal-Knopf, und der meint die ganze
 * Veranstaltung. Menü und Getränke behalten beide — dort ist je Gast der Normalfall.
 */
const PRO_PERSON_SEKTIONEN: readonly string[] = ["menu", "drinks"];

/**
 * Die Knöpfe des Preismodell-Umschalters für EINE Sektion — FEST, unabhängig vom Zustand.
 *
 * Antoine, 17.09.2026, zur ersten Fassung dieser Funktion: „Wenn ich eine Summe eingebe, kommt
 * plötzlich ein drittes Feld, wähle ich etwas, verschwindet das. Das ist verwirrend."
 *
 * Er hatte recht, und der Fehler war ein gut gemeinter Bestandsschutz: Die Reihe zeigte einen
 * ZUSÄTZLICHEN Knopf, solange eine Sektion noch den alten Modus `total` trug, und benannte den
 * Gesamt-Knopf dann auch noch um („Gesamtpreis" ↔ „Pauschale gesamt"). Ein Klick machte aus drei
 * Knöpfen zwei mit anderer Beschriftung. Ein Bedienelement, das seine Form ändert, während man
 * es benutzt, ist schlimmer als der Zustand, den es erklären wollte.
 *
 * Jetzt: Menü und Getränke haben IMMER drei Knöpfe, alle anderen IMMER zwei. Nichts taucht auf,
 * nichts verschwindet, nichts heißt plötzlich anders.
 *
 * Der Altbestand (vier Zeilen mit `total` in equipment/staff/other, drei davon in bereits
 * versendeten Vorgängen) wird weiterhin NICHT still umgedeutet — das wäre eine Preisänderung an
 * echten Kundenvorgängen. Er bleibt stattdessen an der Stelle sichtbar, an der er zählt: die
 * Kopfzeile schreibt dort weiter „X € × N Gäste → Y €" statt „X € für die ganze Veranstaltung",
 * und `istAktiv` unten markiert den Gesamtpreis-Knopf als gewählt. Ein Klick darauf stellt die
 * Zeile bewusst auf `total_event` um, und die Kopfzeile ändert sich sichtbar mit.
 */
function pauschalKnoepfe(section: string, proEinheitLabel: string): { key: SektionsPreisModus; label: string }[] {
  return PRO_PERSON_SEKTIONEN.includes(section)
    ? [
        { key: "per_course", label: proEinheitLabel },
        { key: "total", label: "Pauschale je Gast" },
        { key: "total_event", label: "Pauschale gesamt" },
      ]
    : [
        { key: "per_course", label: proEinheitLabel },
        // sql/445: der Wunsch des Betreibers vom 17.09.2026 — eine Raummiete mit Mindestumsatz
        // ändert sich nicht, wenn zwei Gäste mehr kommen. Hier heißt der Knopf schlicht so, wie
        // der Betrieb ihn kennt; es gibt keinen zweiten Pauschal-Knopf, von dem er sich
        // unterscheiden müsste.
        { key: "total_event", label: "Gesamtpreis" },
      ];
}

/**
 * Ist DIESER Knopf der gewählte? Normalerweise die schlichte Gleichheit — mit einer Ausnahme:
 * eine Bestandszeile mit `total` in einer Sektion, die „je Gast" gar nicht mehr anbietet. Sie
 * rechnet pauschal, also muss der Pauschal-Knopf gewählt aussehen; welche Pauschale gemeint ist,
 * sagt die Kopfzeile der Sektion. Ohne diesen Zweig stünde dort eine Sektion im Pauschalmodus,
 * bei der kein einziger Knopf gewählt wäre.
 */
function istAktiv(knopf: SektionsPreisModus, sektionsModus: SektionsPreisModus, section: string): boolean {
  if (knopf === sektionsModus) return true;
  return knopf === "total_event" && sektionsModus === "total" && !PRO_PERSON_SEKTIONEN.includes(section);
}

/**
 * Punkt 75, Schritt 2 (Entwurf 41a): die Fußzeile je Abschnitt.
 *
 * Der Entwurf setzt sie unter jeden Abschnitt: Gäste, Gänge, Allergen-Überblick, **Preis pro
 * Person** und Zwischensumme. Der Preis pro Person ist dabei die zweite Hälfte von Befund ⑧
 * („weder Gang-Zwischensummen noch Preis pro Person") — die erste, die Gang-Zwischensummen, kam
 * in Schritt 1.
 *
 * **Jede Zahl hier ist bereits gerechnet.** Die Summe ist dieselbe `sektionsSumme`, die oben im
 * Kopf und unten in der Zusammenfassung steht; der Preis pro Person ist diese Summe geteilt durch
 * die Gästezahl; die Allergene kommen aus derselben Aggregation wie überall sonst
 * (`aggregateAllergens`, inklusive ihrer LMIV-Regel, dass EINE ungepflegte Position das ganze
 * Aggregat unvollständig macht). Es entsteht keine neue Rechenstelle — und damit auch keine
 * zweite Zahl, die von der ersten abweichen könnte.
 *
 * Der Preis pro Person steht bewusst NICHT da, wenn keine Gäste erfasst sind: eine Division durch
 * null ergäbe eine Zahl, die nichts bedeutet.
 */
function AbschnittsFuss({
  secItems,
  gaeste,
  summeCents,
  lmivByCatalogId,
  mitAllergenen,
  testId,
}: {
  secItems: OfferItem[];
  gaeste: number;
  summeCents: number;
  lmivByCatalogId: Map<string, { allergens: string[] | null; allergensCheckedAt?: string | null; dietary: Record<string, boolean> }>;
  /** Allergene gehören unter Speisen und Getränke — unter Personal oder Mobiliar wären sie Unsinn. */
  mitAllergenen: boolean;
  testId: string;
}) {
  if (secItems.length === 0) return null;
  const gaenge = gruppiereGaenge(secItems).filter((g) => g.label != null).length;
  /*
   * Entwurf 43 nennt in der Fußzeile „18 Gäste · 4 Gänge · 2 Wahlgänge". Die ersten beiden
   * Zahlen standen schon da, die dritte fehlte — und sie ist die einzige, die man nicht durch
   * Hinsehen zählen kann: eine Wahlgruppe erkennt man an den „oder"-Marken ZWISCHEN Positionen,
   * und die muss man erst suchen.
   *
   * Gezählt werden VERSCHIEDENE Gruppen, nicht Positionen: drei Hauptgänge zur Wahl sind EIN
   * Wahlgang, keine drei. Genau diese Verwechslung wäre die naheliegende gewesen.
   */
  const wahlgaenge = new Set(secItems.map((it) => it.choiceGroupId).filter((id): id is string => id != null)).size;
  const allergene = mitAllergenen
    ? aggregateAllergens(
        secItems.map((it) => {
          const l = it.catalogItemId ? lmivByCatalogId.get(it.catalogItemId) : undefined;
          return { allergens: l ? l.allergens : null, checked: l?.allergensCheckedAt != null };
        }),
      )
    : null;
  const proPerson = gaeste > 0 ? Math.round(summeCents / gaeste) : null;
  return (
    <div className={`flex flex-wrap items-center gap-x-[14px] gap-y-1 ${E41A.fussZeile}`} data-testid={testId}>
      <span>
        {secItems.length} {secItems.length === 1 ? "Position" : "Positionen"}
      </span>
      {gaenge > 0 && (
        <>
          <span aria-hidden>·</span>
          <span>
            {gaenge} {gaenge === 1 ? "Gang" : "Gänge"}
          </span>
        </>
      )}
      {wahlgaenge > 0 && (
        <>
          <span aria-hidden>·</span>
          <span title="Gänge, in denen der Gast zwischen Alternativen wählt">
            {wahlgaenge} {wahlgaenge === 1 ? "Wahlgang" : "Wahlgänge"}
          </span>
        </>
      )}
      {gaeste > 0 && (
        <>
          <span aria-hidden>·</span>
          <span>{gaeste} Gäste</span>
        </>
      )}
      {allergene && (
        <>
          <span aria-hidden>·</span>
          <span title={allergene.union.map((a) => lmivLabel(a)).join(", ") || undefined}>
            {/* Die LMIV-Regel bleibt gewahrt: „keine Allergene" wird nur behauptet, wenn JEDE
                Position gepflegt ist. Eine einzige ungepflegte macht die Aussage unmöglich — dann
                steht hier, dass sie offen ist, und nicht das Gegenteil. */}
            {!allergene.resolved
              ? "Allergene unvollständig"
              : allergene.union.length === 0
                ? "Keine Allergene"
                : `Allergene ${allergene.union.join(", ")}`}
          </span>
        </>
      )}
      <span className="ml-auto flex items-center gap-[14px]">
        {/* Entwurf 41a: der Wert ist kraeftig, das Wort davor bleibt leise — „Pro Person 126,68 €". */}
        {proPerson != null && (
          <span>
            Pro Person <strong className="font-bold text-[#262119] dark:text-inverse-on-surface">{money(proPerson)}</strong>
          </span>
        )}
        <span>
          Zwischensumme{" "}
          <strong className="font-bold tabular-nums text-[#262119] dark:text-inverse-on-surface">{money(summeCents)}</strong>
        </span>
      </span>
    </div>
  );
}

export function PositionsListe({
  editable,
  nurLesen = false,
  visibleItems,
  catalogItems,
  sektionsSumme,
  sektionsUst,
  guestsForItem,
  guestCount,
  lmivByCatalogId,
  openSection,
  onSelectOpenSection,
  option,
  onSetPricingMode,
  onSetMenuTotalPrice,
  onSetSectionPricing,
  setError,
  bearbeiteteAbschnitte,
  onAbschnittBearbeiten,
  openPositionDrawer,
  patchItem,
  removeItem,
  onDragEnd,
  imPaketIds = new Set<string>(),
  paketAufteilung,
  onPaketUebernehmen,
}: {
  editable: boolean;
  /**
   * Bugfund 02.11.2026: angenommene Fassung im gesperrten Status - die Positionen stehen LESEND da (statt gar nicht),
   * jedes Bedienelement bleibt aus (`editable` ist dabei false).
   */
  nurLesen?: boolean;
  visibleItems: OfferItem[];
  /** Katalog-Vorschläge im Bezeichnungsfeld (ItemRow) — dieselbe Quelle wie im
   *  „Position hinzufügen"-Drawer (`PositionHinzufuegenDrawer`), nur ungefiltert durchgereicht. */
  catalogItems: CatalogItem[];
  sektionsSumme: (section: OfferSection) => number;
  sektionsUst: (secItems: OfferItem[]) => string;
  guestsForItem: (it: OfferItem) => number;
  /** R34-1: dieselbe `fallbackGuests`-Zahl wie in `builder.tsx` (Options-Gaestezahl mit
   *  Event-Fallback) — fuer den "Aus Positionen"-Vorschlag (Positions-Summe / Gaestezahl =
   *  Vorschlag fuer den Menuepreis PRO PERSON), kein eigener Rechenweg. */
  guestCount: number;
  lmivByCatalogId: Map<string, { allergens: string[] | null; allergensCheckedAt?: string | null; dietary: Record<string, boolean> }>;
  openSection: OfferSection;
  onSelectOpenSection: (section: OfferSection) => void;
  option: Pick<OfferOption, "pricingMode" | "menuTotalPriceCents" | "sectionPricing" | "aLaCarte"> | null;
  onSetPricingMode: (mode: "per_course" | "total" | "total_event") => Promise<void>;
  onSetMenuTotalPrice: (cents: number | null) => Promise<void>;
  onSetSectionPricing: (section: string, mode: "per_course" | "total" | "total_event", totalPricePerPersonCents: number | null) => Promise<void>;
  setError: (message: string) => void;
  bearbeiteteAbschnitte: Set<OfferSection>;
  onAbschnittBearbeiten: (section: OfferSection, an: boolean) => void;
  /**
   * `einfuegen` (Mockup v3, Einfügelinie): die neue Position kommt direkt HINTER `nachId` und
   * trägt `courseLabel` — so landet „+ Position" im Gang, an dessen Ende sie angeboten wurde, und
   * „+ Gang" beginnt dort einen neuen Gang. Ohne Angabe wie bisher ans Ende des Abschnitts.
   */
  openPositionDrawer: (section: OfferSection, einfuegen?: { nachId: string; courseLabel: string | null }) => void;
  patchItem: (itemId: string, patch: Partial<OfferItem>) => void;
  removeItem: (itemId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  /** Mockup v3: Positionen, deren Preis im Paketpreis steckt (Regel aus `@maestro/pricing`, in
   *  `builder.tsx` über die GANZE Variante bestimmt). Sie zeigen „im Paket" statt „à 0,00 €". */
  imPaketIds?: ReadonlySet<string>;
  /** Punkt R: „davon Speisen / davon Getränke" je Paket-Kopfzeile (aus `paketTeile`, builder.tsx). */
  paketAufteilung?: ReadonlyMap<string, { teile: PauschalTeil[]; ausPaket: boolean }>;
  /** Punkt X (Antoine 13.10.2026: „Ja, Knopf zeigen"): im ganz leeren Angebot steht „Paket
   *  übernehmen" neben den „+"-Chips. Öffnet denselben Pakete-Reiter wie die Chip-Leiste. */
  onPaketUebernehmen?: () => void;
}) {
  /**
   * Builder v3 / B-4 (Entwurf 31b): EINE Zeile bearbeiten, ohne den ganzen Abschnitt umzuschalten.
   * Schritt 11 (Phase B): vollstaendig lokal -- keine andere Komponente liest oder schreibt
   * `bearbeiteteZeilen`/`zeilenZustandRef`.
   */
  const [bearbeiteteZeilen, setBearbeiteteZeilen] = useState<Set<string>>(new Set());
  /**
   * Der Stand einer Zeile im Moment des Oeffnens — die Grundlage fuer „Abbrechen". Der Builder
   * speichert beim Tippen (Autosave, 800 ms); „Abbrechen" setzt die Felder deshalb auf den
   * gemerkten Stand zurueck und laesst denselben Autosave laufen (ueber `patchItem`, Prop) --
   * derselbe Weg zum Server wie bei jeder anderen Aenderung, nur ist es der alte Wert, der ihn
   * geht.
   */
  const zeilenZustandRef = useRef(new Map<string, ZeilenStand>());
  /** Mockup 34a (erst 24.08.2026 erstmals einsehbar): expliziter „Übernehmen"-Knopf neben dem
   *  Preis-Eingabefeld. Das Feld bleibt unveraendert unkontrolliert (`defaultValue`, wie
   *  zuvor) und speichert weiterhin automatisch bei `onBlur` — der Knopf ist kein zweiter
   *  Speicherweg, nur derselbe Commit auf einen expliziten Klick hin, wie im Mockup gezeigt.
   *  Bughunt 27.08.2026: EIN Ref je Sektion (statt eines einzelnen) — seit Getraenke denselben
   *  Umschalter tragen wie Menue, koennen beide Eingabefelder gleichzeitig im DOM stehen. */
  const gesamtpreisInputRefs = useRef<Partial<Record<string, HTMLInputElement | null>>>({});
  /**
   * Punkt 75, Schritt 2 (Entwurf 41a, Befund ②): „Das Preismodell-Segment belegt eine ganze Zeile
   * je Abschnitt, wird aber selten umgeschaltet."
   *
   * Der Befund stimmt — aber nur für den Normalfall. Steht ein Abschnitt auf „Preis pro Gang",
   * trägt der Streifen nichts als drei Knöpfe, die der Betrieb einmal im Jahr anfasst. Steht er
   * auf einer PAUSCHALE, trägt er das Preisfeld, den Vorschlag „Aus Positionen", den
   * „Übernehmen"-Knopf und die Erklärung, warum die Einzelpreise nicht ins Angebot fließen — dann
   * ist er kein Rauschen, sondern das Wichtigste am ganzen Abschnitt.
   *
   * Also: im Normalfall schrumpft er auf einen Chip im Abschnittskopf, der das gewählte Modell
   * NENNT und ihn auf Klick wieder ausklappt. Im Pauschalfall bleibt er offen wie bisher. Es geht
   * keine Funktion verloren — sie ist einen Klick weiter, dort wo sie hingehört. (Derselbe
   * Gedanke wie bei Befund ①: der Normalfall schrumpft, die Ausnahme bleibt sichtbar.)
   */
  const [preismodellOffen, setPreismodellOffen] = useState<Set<string>>(new Set());
  const preismodellUmschalten = (key: string) =>
    setPreismodellOffen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const zeileOeffnen = (it: OfferItem) => {
    zeilenZustandRef.current.set(it.id, {
      name: it.name,
      qtyMilli: it.qtyMilli,
      unitPriceCents: it.unitPriceCents,
      priceMode: it.priceMode,
      vatRateBp: it.vatRateBp,
    });
    setBearbeiteteZeilen((prev) => new Set(prev).add(it.id));
  };
  const zeileSchliessen = (itemId: string) => {
    zeilenZustandRef.current.delete(itemId);
    setBearbeiteteZeilen((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  };
  const zeileAbbrechen = (itemId: string) => {
    const stand = zeilenZustandRef.current.get(itemId);
    // Kein Sonderweg: das Zuruecksetzen ist eine ganz normale Aenderung ueber `patchItem`.
    if (stand) patchItem(itemId, stand);
    zeileSchliessen(itemId);
  };

  /**
   * Fix „Sektions-/Zeilen-Bearbeitung, Runde 2026-08-28": `bearbeiteteZeilen`/`zeilenZustandRef`
   * kennen den Sektions-Umschalter nicht (und umgekehrt, siehe `abschnittBearbeiten` in
   * `builder.tsx`) — schaltete man eine Zeile einzeln auf, dann die ganze Sektion auf „Alle
   * bearbeiten" und wieder zurueck auf „Fertig", blieb die Zeile in `bearbeiteteZeilen` mit
   * ihrem ALTEN `zeilenZustandRef`-Snapshot stehen: `einzeln` wurde erneut wahr
   * (`!bearbeiteteAbschnitte.has(key) && bearbeiteteZeilen.has(it.id)`), die Zeile erschien
   * wieder einzeln offen, und ein Klick auf „Abbrechen" verwarf still jede Aenderung, die
   * WAEHREND der Sektions-Bearbeitung an ihr gemacht wurde. Der Sektions-Umschalter muss darum
   * bei JEDER Betaetigung die Zeilen-Buchfuehrung der eigenen Sektion raeumen — unabhaengig
   * davon, ob er sie ein- oder ausschaltet: Autosave bleibt unberuehrt (es wird nichts
   * verworfen), nur die „Abbrechen"-Merkzeile wird zurueckgesetzt. Oeffnet der Nutzer die Zeile
   * danach erneut einzeln per Stift, bekommt sie einen frischen Snapshot.
   */
  const sektionUmschalten = (section: OfferSection, an: boolean) => {
    const betroffen = visibleItems.filter((it) => it.section === section).map((it) => it.id);
    if (betroffen.length > 0) {
      setBearbeiteteZeilen((prev) => {
        const next = new Set(prev);
        let geaendert = false;
        for (const id of betroffen) {
          if (next.delete(id)) geaendert = true;
        }
        return geaendert ? next : prev;
      });
      for (const id of betroffen) zeilenZustandRef.current.delete(id);
    }
    onAbschnittBearbeiten(section, an);
  };

  /**
   * Umsortieren per Drag-and-Drop (dnd-kit). Maus: 4 px Bewegung (die ganze Zeile ist seit
   * 23.08.2026 der Griff, s. ItemRow.tsx). Finger: nur der Griff, und erst nach kurzem Halten
   * (`DND_TOUCH_HALTEN`) — sonst startete jedes Wischen am linken Rand einen Drag (16.10.2026); `KeyboardSensor` mit
   * `sortableKeyboardCoordinates` ist der Tastatur-Weg fuer `useSortable` (Leertaste hebt die
   * fokussierte Zeile, Pfeiltasten verschieben sie) — die vormaligen expliziten Auf/Ab-Knoepfe
   * (`moveItemInSection`) sind damit redundant und entfallen.
   */
  const dndSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_HALTEN }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return editable || nurLesen ? (
    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
    {SECTIONS.map(({ key, label, icon }) => {
      const secItems = visibleItems.filter((it) => it.section === key);
      const secIds = secItems.map((it) => it.id);
      // Mockup v3: leere Abschnitte stehen nicht mehr als eigene Karten da, sondern gesammelt in
      // EINER Zeile „Weitere Positionen" am Ende (siehe unten). Hat ein Abschnitt Inhalt, wird
      // er ein eigener Abschnitt wie „Menü".
      if (secItems.length === 0) return null;
      const isOpenMobile = openSection === key;
      /*
       * Mockup v3: die bepreiste Paket-Kopfzeile (`packageId`, kein Katalogbezug, Preis > 0 —
       * genau die Zeile, die `paket-anwenden.ts` anlegt und die `ungedeckteNullpreisPositionen`
       * als Deckungsnachweis liest) steht als Kopf ÜBER den Gängen, nicht als Gang-Zeile. Die
       * Gänge und Trenner rechnen deshalb mit `listItems` — sonst hinge der erste Gang per
       * „und/oder" an der Kopfzeile.
       */
      const istPaketKopf = (it: OfferItem) => it.packageId != null && it.catalogItemId == null && it.unitPriceCents > 0;
      const paketKoepfe = secItems.filter(istPaketKopf);
      const listItems = secItems.filter((it) => !istPaketKopf(it));
      const enthaltenSatz = (kopf: OfferItem): string | null => {
        const teile = listItems.filter((it) => it.packageId === kopf.packageId);
        if (teile.length === 0 || !teile.every((it) => imPaketIds.has(it.id))) return null;
        const gaenge = new Set(teile.map((it) => it.courseLabel?.trim()).filter(Boolean)).size;
        return gaenge > 1 ? `Alle ${gaenge} Gänge sind im Paketpreis enthalten.` : teile.length > 1 ? `Alle ${teile.length} Positionen sind im Paketpreis enthalten.` : "Im Paketpreis enthalten.";
      };
      // §5.9: Wie viele Zeilen dieses Abschnitts tragen den Chip „kein Preis"? Der
      // R34-1 (Konzept § 2, "0,00 €" statt Amber-Pill): ein leeres Pflichtfeld bedeutet nie
      // stumm 0 € — im Gesamtpreis-Modus OHNE eingetragenen Menüpreis rechnet `sektionsSumme`
      // (builder.tsx) den Abschnitt korrekt mit 0 €, weil der Wert schlicht fehlt. Das ist kein
      // Zahlenfehler, nur eine Anzeige, die eine Lücke wie einen echten Nullbetrag aussehen
      // laesst. Dieselbe Bedingung wie `preisFehlt`/`ohnePreis` in `ItemRow.tsx`, nur auf
      // Sektionsebene.
      // Bughunt 27.08.2026: Preismodell-Zustand dieser Sektion, generisch — Menue bleibt an den
      // eigenen Options-Feldern (unveraendert), andere gesamtpreisfaehige Sektionen (aktuell nur
      // drinks, s. GESAMTPREIS_SEKTIONEN) an `option.sectionPricing`.
      const gesamtpreisWortliste = GESAMTPREIS_SEKTIONEN[key];
      // sql/445: `alsSektionsModus` normalisiert fehlend/unbekannt auf `per_course` — dieselbe
      // Umdeutung wie Server, Dokument und Rechnung sie benutzen, statt einer vierten Lesart.
      const sektionsModus = alsSektionsModus(key === "menu" ? option?.pricingMode : option?.sectionPricing?.[key]?.mode);
      const sektionsTotalCents = key === "menu" ? option?.menuTotalPriceCents : (option?.sectionPricing?.[key]?.totalPricePerPersonCents ?? null);
      const istPauschal = istPauschalerModus(sektionsModus);
      const menuPreisFehlt = gesamtpreisWortliste != null && istPauschal && sektionsTotalCents == null;
      // R34-3: der Gegenzustand zu `menuPreisFehlt` — die Pauschale ist eingetragen. Beide
      // schliessen sich gegenseitig aus (dasselbe Feld), gebraucht fuer den Rechenweg im Kopf
      // (Stueck A) und den Kalkulationsabstand (Stueck B).
      const pauschaleGesetzt = gesamtpreisWortliste != null && istPauschal && sektionsTotalCents != null;
      // "Aus Positionen": dieselbe Summe, die `sektionsSumme(key)` im Preis-pro-Gang-Modus
      // anzeigen wuerde (lineGross/toEngineItems, wie `builder.tsx`s `sectionSubtotals` — kein
      // zweiter Rechenweg, nur ohne den Gesamtpreis-Kollaps).
      //
      // sql/445: DER VORSCHLAG MUSS IN DER EINHEIT DES MODUS STEHEN, sonst schlaegt der Knopf
      // einen Betrag vor, den das Eingabefeld anders liest als er gemeint war:
      //   `total`        Betrag JE GAST  -> durch die Gaestezahl teilen (lineGross hat bei
      //                                     per_person-Zeilen bereits multipliziert)
      //   `total_event`  Betrag INSGESAMT -> die Summe unveraendert uebernehmen
      const ausPositionenSummeCents =
        gesamtpreisWortliste != null && istPauschal
          ? toEngineItems(secItems).reduce((sum, engineItem, idx) => sum + lineGross(engineItem, guestsForItem(secItems[idx]!)), 0)
          : null;
      const ausPositionenCents =
        ausPositionenSummeCents == null
          ? null
          : sektionsModus === "total_event"
            ? ausPositionenSummeCents
            : guestCount > 0
              ? Math.round(ausPositionenSummeCents / guestCount) // Division durch 0 vermeiden
              : null;
      // Migration 384: EINE gemeinsame Zeilen-Render-Funktion fuer Einzelzeilen UND Mitglieder
      // einer Wahlgruppe — kein zweiter ItemRow-Aufbau, nur ob ein Rahmen drumherum steht.
      const renderItemRow = (it: OfferItem, inWahlgruppe = false, neuerGangAbHier?: () => void, paketKopf?: { enthalten: string | null; aufteilung?: { teile: PauschalTeil[]; ausPaket: boolean } }) => (
        <ItemRow
          key={it.id}
          item={it}
          imPaket={imPaketIds.has(it.id)}
          nachKarte={option?.aLaCarte === true}
          paketKopf={paketKopf}
          onNeuerGangAbHier={editable ? neuerGangAbHier : undefined}
          /* Entwurf 43: in einer Wahlgruppen-Karte steht links ein Radio-Ring statt der
             Griff-Punkte — es geht dort um eine Wahl, nicht um eine Reihenfolge. */
          inWahlgruppe={inWahlgruppe}
          section={key}
          catalogItems={catalogItems}
          guests={guestsForItem(it)}
          /* Umsortieren: die ganze Zeile ist der Drag-Griff (dnd-kit, useSortable in
             ItemRow) — Pfeil-Fallback entfaellt seit 23.08.2026 zugunsten von dnd-kits
             eigenem KeyboardSensor, s. Kommentar dort. Ohne editable gibt es hier
             gar keine Zeile (siehe oben), darum ist `umsortierbar` unbedingt gesetzt. */
          umsortierbar
          lmiv={it.catalogItemId ? lmivByCatalogId.get(it.catalogItemId) : undefined}
          /* Bis 03.08.2026 stand eine Position ohne Preis (K1.0) sofort als
             Formular da - gut gemeint, aber am echten Angebot zerriss es die Seite:
             Storias Getraenke-Abschnitt besteht aus VIER preislosen Wunschpositionen,
             also vier aufgeklappten Formularen neben einer ruhigen Menue-Liste. Genau
             das war Antoines "Getraenke ist anders als Menue". Die fehlende Arbeit
             wird jetzt IN der Lesezeile markiert (siehe ItemRow) - sichtbarer als ein
             leeres Eingabefeld und ohne den Abschnitt zu sprengen. */
          /* B-4: offen ist eine Zeile aus zwei moeglichen Gruenden — der Abschnitt
             steht auf „Alle bearbeiten" ODER sie wurde einzeln aufgeklappt (31b).
             Das Formular dahinter ist in beiden Faellen dasselbe. */
          bearbeiten={bearbeiteteAbschnitte.has(key) || bearbeiteteZeilen.has(it.id)}
          einzeln={!bearbeiteteAbschnitte.has(key) && bearbeiteteZeilen.has(it.id)}
          /* R34-2, eingegrenzt auf Menü (Antoine 28.08.2026): die Rechenregel
             `istSektionsKern` in `packages/pricing/src/index.ts` kollabiert seit der
             Verallgemeinerung (Bughunt 27.08.2026) JEDE gesamtpreisfaehige Sektion
             gleichermassen — das bleibt unveraendert, die Summe zaehlt weiterhin nur
             die Pauschale, nicht die Einzelzeilen. Nur die DARSTELLUNG hier zeigt das
             ausschliesslich fuer Menü gedaempft/durchgestrichen; Getraenke-Positionen
             sehen im Gesamtpreis-Modus jetzt genauso "normal" aus wie Menü-Positionen
             im Preis-pro-Gang-Modus — die durchgestrichenen Preise + "zählt nicht"
             lasen sich dort wie ein Fehler, nicht wie eine Erklaerung. */
          /*
           * sql/445 + Antoine 17.09.2026: gilt fuer JEDE Sektion mit Pauschale, nicht mehr nur
           * fuers Menue. Die Einschraenkung stammte aus der Zeit der durchgestrichenen Preise
           * (28.08.2026, „bei Getraenken wirkte es wie ein Fehler statt wie eine Erklaerung") —
           * die Durchstreichung ist laengst weg, und ohne sie war das Ergebnis ein Widerspruch:
           * „Pauschalpreis Gesamt bei Blumen 250.- €, trotzdem steht unten 2x 500.- €".
           *
           * `istPauschal` deckt beide Pauschal-Modi ab. Optionale Zeilen bleiben unberuehrt: sie
           * tragen einen eigenen, vom Gast waehlbaren Preis und gehen nicht in der Pauschale auf.
           */
          kollabiert={istPauschal && it.isOptional !== true && it.surcharge !== true}
          pauschaleBlock={istPauschal}
          onPatch={(p) => patchItem(it.id, p)}
          onRemove={() => removeItem(it.id)}
          /* H-12: ohne fertig geladene Variante wird nicht bearbeitet — dann gibt
             es den Knopf gar nicht erst. */
          onOeffnen={editable ? () => zeileOeffnen(it) : undefined}
          onFertig={() => zeileSchliessen(it.id)}
          onAbbrechen={() => zeileAbbrechen(it.id)}
          /* Migration 384: "Aus Wahlgruppe lösen" - patcht choiceGroupId auf null, kein
             Sonderweg (derselbe patchItem wie jede andere Aenderung). */
          onGruppeVerlassen={it.choiceGroupId != null ? () => patchItem(it.id, { choiceGroupId: null }) : undefined}
          /* sql/753: „und“-Verbindung zubuchbarer Positionen = ein Schalter auf der Gastseite. */
          verbindbar={
            it.isOptional === true
              ? visibleItems.filter((o2) => o2.isOptional === true && o2.id !== it.id).map((o2) => ({ id: o2.id, name: o2.name, gruppe: o2.addonGruppe ?? null }))
              : undefined
          }
          onVerbinden={(mitId) => {
            if (mitId == null) {
              patchItem(it.id, { addonGruppe: null });
              return;
            }
            const ziel = visibleItems.find((o2) => o2.id === mitId);
            const gruppe = ziel?.addonGruppe ?? crypto.randomUUID();
            if (!ziel?.addonGruppe) patchItem(mitId, { addonGruppe: gruppe });
            patchItem(it.id, { addonGruppe: gruppe });
          }}
        />
      );
      /*
       * Punkt 75, Schritt 2 (Befund ②): der Name des GEWÄHLTEN Preismodells, für den Chip im
       * Kopf. Er kommt aus derselben Liste, die auch die Knöpfe im Streifen beschriftet
       * (`pauschalKnoepfe`) und derselben Prüfung, die dort den aktiven markiert (`istAktiv`) —
       * kein zweites Vokabular, das auseinanderlaufen könnte.
       */
      /*
       * 12.10.2026 (Antoine, Paket „Gala Silvester Menü", 99 € je Gast): „Das ist ja nicht Preis
       * pro Gang, sondern Pauschale pro Person." Trägt eine Paket-Kopfzeile den Preis des ganzen
       * Abschnitts (alle übrigen Pflicht-Positionen stecken im Paketpreis), dann IST der Abschnitt
       * pauschal — gespeichert bleibt `per_course`, weil die Kopfzeile selbst rechnet (die Summe
       * ändert sich nicht). Der Chip nennt darum die Preisart des Pakets und ist kein Umschalter:
       * ein Klick auf „Pauschale je Gast" würde die Kopfzeile in eine leere Sektions-Pauschale
       * kollabieren lassen.
       */
      // Die Kopfzeile darf auch in einem ANDEREN Abschnitt stehen: die Getränke eines Menü-Pakets
      // (Weinbegleitung) stehen im Getränke-Block, ihr Preis steckt in der Kopfzeile im Menü.
      const paketIdsHier = new Set(listItems.map((it) => it.packageId).filter((v): v is string => v != null));
      const deckendeKoepfe = visibleItems.filter((it) => istPaketKopf(it) && (it.section === key || paketIdsHier.has(it.packageId!)));
      const paketPreisModell =
        gesamtpreisWortliste && !istPauschal && deckendeKoepfe.length > 0 && listItems.every((it) => it.isOptional || imPaketIds.has(it.id))
          ? (pauschalKnoepfe(key, gesamtpreisWortliste.proEinheit).find((m) => m.key === (deckendeKoepfe.every((k) => k.priceMode === "flat") ? "total_event" : "total"))?.label ?? null)
          : null;
      const aktivesPreismodell = gesamtpreisWortliste
        ? (pauschalKnoepfe(key, gesamtpreisWortliste.proEinheit).find((m) => istAktiv(m.key, sektionsModus, key))?.label ??
          gesamtpreisWortliste.proEinheit)
        : null;

      return (
        <Card key={key} className={`overflow-hidden ${E41A.karte}`} data-testid={`builder-section-card-${key}`}>
          <div
            role="button"
            tabIndex={0}
            data-testid={`builder-section-header-${key}`}
            className={`flex w-full cursor-pointer items-center justify-between gap-2 text-left ${E41A.sektionsKopf}`}
            onClick={() => onSelectOpenSection(key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onSelectOpenSection(key);
            }}
          >
            {/* B-5 (31c): `min-w-0` + Umbruch. Ein Flex-Kind behaelt ohne `min-w-0` seine
                Inhaltsbreite — bei 390 px schob dieser Block die drei Knoepfe rechts aus
                der Karte, und die traegt `overflow-hidden`: sie waren abgeschnitten, nicht
                bedienbar. Am Schreibtisch ist davon nichts zu sehen, dort ist Platz. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-beleg text-on-background dark:text-inverse-on-surface">
              {/* Entwurf 41a: das Symbol traegt die Akzentfarbe, der Titel 14,5 px/600. */}
              <Icon name={icon} className="text-[18px] text-[#C2410C] dark:text-primary" />
              <span className="text-[14.5px] font-semibold">{label}</span>
              {/*
                DIE LEGENDE (Entwurf 43): ein kleines Fragezeichen neben dem Abschnittstitel, das
                die drei Zeichen der Liste EINMAL erklärt.

                Der Befund im Entwurf lautete: „das Ketten-Icon steht ohne Erklärung dauerhaft in
                jeder Zeile". Der naheliegende Ausweg wäre gewesen, es zu verstecken — dann fände
                es niemand mehr. Der Entwurf wählt den anderen: die Zeichen bleiben, und eine
                Stelle sagt, was sie bedeuten. Einmal oben, nicht achtzehnmal in den Zeilen.

                `stopPropagation`: der ganze Kopf ist ein Knopf, der den Abschnitt auf- und
                zuklappt. Wer die Legende antippt, will lesen, nicht zuklappen.
              */}
              <span
                data-testid={`builder-legende-${key}`}
                title={LEGENDE_43}
                onClick={(e) => e.stopPropagation()}
                className="grid h-[18px] w-[18px] shrink-0 cursor-default place-items-center rounded-full border border-[#E3DBCB] text-[10.5px] font-semibold text-[#9A9080] dark:border-outline/40 dark:text-secondary-fixed-dim"
              >
                <span aria-hidden>?</span>
                {/* Der Tooltip hängt an `title` und ist für Screenreader nicht verlässlich
                    erreichbar — derselbe Text steht deshalb noch einmal in Worten. */}
                <span className="sr-only">{LEGENDE_43}</span>
              </span>
              {/* 41a: der Zaehler ist ein Chip und NENNT, was er zaehlt — vorher stand dort eine
                  nackte Zahl. */}
              {/* Mockup v3: im Menü zählt der Chip die Gänge („4 Gänge"), sonst die Positionen. */}
              <span className={E41A.chip}>
                {((g) => (g > 0 ? `${g} ${g === 1 ? "Gang" : "Gänge"}` : `${secItems.length} ${secItems.length === 1 ? "Position" : "Positionen"}`))(
                  key === "menu" ? gruppiereGaenge(listItems).filter((g) => g.label != null).length : 0,
                )}
              </span>
              {/*
                Punkt 75, Schritt 2 (Befund ②): der Chip steht stellvertretend für den
                Preismodell-Streifen, solange dieser nichts zu sagen hat. Er NENNT das gewählte
                Modell — vorher stand im Kopf gar nichts dazu, man musste den Streifen lesen — und
                klappt ihn auf Klick wieder aus.

                `stopPropagation`, weil der ganze Kopf ein Knopf ist, der den Abschnitt
                auf-/zuklappt: ohne das täte ein Klick beides auf einmal.
              */}
              {paketPreisModell && (
                <span data-testid={`builder-preismodell-chip-${key}`} className={E41A.chipModell} title="Der Preis kommt aus dem Paket">
                  {paketPreisModell}
                </span>
              )}
              {gesamtpreisWortliste && option && !istPauschal && !paketPreisModell && (
                <button
                  type="button"
                  data-testid={`builder-preismodell-chip-${key}`}
                  aria-expanded={preismodellOffen.has(key)}
                  onClick={(e) => {
                    e.stopPropagation();
                    preismodellUmschalten(key);
                  }}
                  className={E41A.chipModell}
                  title="Preismodell dieses Abschnitts ändern"
                >
                  {aktivesPreismodell}
                  <span aria-hidden>
                    <Icon name={preismodellOffen.has(key) ? "expand_less" : "expand_more"} className="text-[14px]" />
                  </span>
                </button>
              )}
              {/* Der Zähler „N ohne Preis" stand hier bis zum 17.09.2026 (Antoine: „nimm alle
                  diese Warnungen weg"). Eine Position ohne Preis ist im Gastronomie-Alltag der
                  Normalfall, nicht die Ausnahme. */}
              {/* Builder v3 / B-3 (Entwurf 31a: „Menü · 4 Positionen · 7 % USt · 1.464,00 €").
                  Beides wurde laengst gerechnet - der USt-Satz steht an jeder Zeile, die
                  Abschnittssumme in `sectionSubtotals` fuer die Zusammenfassung. Sichtbar
                  war es nur ganz unten; wer eine Sektion bearbeitet, sieht jetzt sofort,
                  was sie kostet. Mehrere Saetze in einer Sektion (z. B. Speise + Getraenk)
                  werden ehrlich als "gemischt" ausgewiesen statt einen davon zu behaupten. */}
              {/* B-5 (31c): Bis 11.08.2026 war dieser ganze Block unter 640 px ausgeblendet
                  — am Handy stand im Kopf also keine Abschnittssumme, obwohl gerade dort
                  alles andere eingeklappt ist und man sonst bis ganz nach unten muss.
                  Zurueck tritt jetzt nur der USt-Satz: die Summe traegt die Aussage, der
                  Satz ist die Erlaeuterung dazu. */}
              {secItems.length > 0 && (
                <span className="flex items-center gap-2 text-beleg-rechenweg text-on-surface-variant">
                  <span aria-hidden="true">·</span>
                  <span className="hidden sm:inline">{sektionsUst(secItems)}</span>
                  <span className="hidden sm:inline" aria-hidden="true">·</span>
                  {/* R34-1: derselbe Amber-Pill wie "Preis fehlt" je Zeile (`ItemRow.tsx`,
                      `bg-[#F0EAE0]`/`text-secondary`) — hier auf Sektionsebene statt still
                      0,00 € zu zeigen, waehrend der Menüpreis noch fehlt. */}
                  {menuPreisFehlt ? (
                    <span
                      className="whitespace-nowrap rounded bg-[#F0EAE0] px-2 py-0.5 text-beleg-notiz text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
                      title={`Der ${gesamtpreisWortliste!.preiswort} ${sektionsModus === "total_event" ? "für die ganze Veranstaltung" : "pro Person"} fehlt noch. Ohne ihn zählt dieser Abschnitt mit 0,00 € in die Summe.`}
                    >
                      {gesamtpreisWortliste!.preiswort} fehlt
                    </span>
                  ) : pauschaleGesetzt ? (
                    /* R34-3 Stueck A (Konzept § 2 "Pauschale-Rechenweg im Kopf", § 4 R34-3 —
                       "Rechenweg im Kopf: „45,00 € × 67 Gäste → 3.015,00 €" statt Selbst-
                       Herleitung."): dieselbe Gesamtsumme wie unten (`sektionsSumme(key)`, kein
                       zweiter Rechenweg), nur zusaetzlich mit den beiden Eingabewerten davor —
                       der Operator muss die Multiplikation nicht mehr im Kopf machen. */
                    <span
                      className="whitespace-nowrap text-beleg-betrag text-on-background dark:text-inverse-on-surface"
                      title={
                        sektionsModus === "total_event"
                          ? `Rechenweg: ${gesamtpreisWortliste!.preiswort} für die ganze Veranstaltung — die Gästezahl ändert ihn nicht`
                          : `Rechenweg: ${gesamtpreisWortliste!.preiswort} pro Person × Gästezahl`
                      }
                    >
                      {/* sql/445: Bei `total_event` stünde hier sonst genau der gemeldete Fehler
                          in der Oberfläche — „6.500,00 € × 12 Gäste → 78.000,00 €". */}
                      {sektionsModus === "total_event"
                        ? `${money(sektionsTotalCents!)} für die ganze Veranstaltung`
                        : `${money(sektionsTotalCents!)} × ${guestCount} Gäste → ${money(sektionsSumme(key))}`}
                    </span>
                  ) : (
                    <span className="text-beleg-betrag text-on-background dark:text-inverse-on-surface" title="Summe dieses Abschnitts">
                      {/* Mockup v3: steckt der ganze Abschnitt in einem Paket, das hier nicht
                          selbst bepreist ist (z. B. Wasser im Menüpaket), sagt der Kopf das. */}
                      {paketKoepfe.length === 0 && listItems.length > 0 && listItems.every((it) => imPaketIds.has(it.id)) ? "im Paket" : money(sektionsSumme(key))}
                    </span>
                  )}
                </span>
              )}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              {/* Builder v3 / B-4 (Entwurf 31a): Der Umschalter steht im Entwurf genau hier,
                  links neben „+ Position". Er aendert nur die Darstellung des Abschnitts —
                  gespeichert wird unveraendert beim Tippen (Autosave). */}
              {editable && secItems.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  className="!px-2 !py-1 text-beleg-mikro text-on-surface-variant"
                  /* B-5 (31c): unter 640 px bleibt nur das Symbol stehen — sonst passen die
                     drei Steuerelemente nicht neben den Abschnittsnamen. Der Name des
                     Knopfes darf dabei nicht verlorengehen, sonst ist er zwar da, aber
                     fuer Vorlesewerkzeuge stumm; er wandert deshalb ins `aria-label`. */
                  aria-label={
                    bearbeiteteAbschnitte.has(key)
                      ? `Bearbeitung im Abschnitt ${label} beenden`
                      : `Alle Positionen im Abschnitt ${label} bearbeiten`
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    sektionUmschalten(key, !bearbeiteteAbschnitte.has(key));
                  }}
                >
                  {bearbeiteteAbschnitte.has(key) ? (
                    <>
                      <Icon name="check" className="text-[16px]" /> <span className="hidden sm:inline">Fertig</span>
                    </>
                  ) : (
                    <>
                      <Icon name="edit" className="text-[16px]" /> <span className="hidden sm:inline">Alle bearbeiten</span>
                    </>
                  )}
                </Button>
              )}
              {editable && (
                <Button
                  type="button"
                  className="!px-2.5 !py-1 text-[12px]"
                  aria-label={`Position im Abschnitt ${label} hinzufügen`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openPositionDrawer(key);
                  }}
                >
                  <Icon name="add" className="text-[16px]" /> <span className="hidden sm:inline">Position</span>
                </Button>
              )}
              <Icon name={isOpenMobile ? "expand_less" : "expand_more"} className="text-[18px] text-on-surface-variant/60 lg:hidden" />
            </span>
          </div>
          {/* KONZEPT-GESAMTPREIS-PRO-MENUE, verallgemeinert (Bughunt 27.08.2026): Umschalter
              Preis-je-Position <-> Gesamtpreis, an jeder gesamtpreisfaehigen Sektion
              (GESAMTPREIS_SEKTIONEN — aktuell Menue + Getraenke; "pro Menue"/"pro Getraenke" und
              "pro Variante" fallen in diesem Schema zusammen, da es je Option genau eine Zeile
              pro Sektion gibt). Aendert nur den Rechenweg (siehe `breakdown`-useMemo in
              builder.tsx); die Einzel-Zeilen selbst bleiben unveraendert stehen, ein
              Zurueckschalten verliert also keine eingetragenen Einzelpreise. Menue schreibt
              weiterhin ueber `onSetPricingMode`/`onSetMenuTotalPrice` (PATCH .../offers/:id,
              unveraendert seit 20.08.2026), andere Sektionen ueber `onSetSectionPricing`
              (POST .../offers/:id/section-pricing, Migration 100). */}
          {gesamtpreisWortliste && option && (istPauschal || preismodellOffen.has(key)) && (
            <div className="flex flex-wrap items-center gap-3 border-b border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 dark:border-outline/30 dark:bg-night-card">
              <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Preismodell</span>
              <div className="flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-surface-variant/20">
                {pauschalKnoepfe(key, gesamtpreisWortliste.proEinheit).map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    disabled={!editable}
                    aria-pressed={istAktiv(m.key, sektionsModus, key)}
                    className={`rounded-md px-2.5 py-1.5 font-label-md text-[11.5px] transition-colors disabled:pointer-events-none disabled:opacity-50 ${
                      istAktiv(m.key, sektionsModus, key)
                        ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                        : "text-on-surface-variant dark:text-secondary-fixed-dim"
                    }`}
                    onClick={() => void (key === "menu" ? onSetPricingMode(m.key) : onSetSectionPricing(key, m.key, sektionsTotalCents ?? null))}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {istPauschal && (
                /* R34-1: Erklärstreifen mit Inline-Eingabe — dieselbe Eingabe wie zuvor (Logik
                   unveraendert), nur je nach Zustand des Pflichtfelds anders gerahmt. Fehlt der
                   Preis, erklaert der Streifen, WARUM der Abschnitt oben als Amber-Pill statt
                   Betrag steht, und bietet direkt daneben den "Aus Positionen"-Vorschlag an. */
                <div
                  className={`flex flex-col gap-1.5 ${menuPreisFehlt ? "rounded-md bg-[#F0EAE0] px-2 py-1.5 dark:bg-white/[0.08]" : ""}`}
                >
                  {/* Mockup 34a: dauerhafter Erklaertext ueber der Eingabe, nicht nur die kurze
                      Amber-Zeile — sagt, WARUM ein Pauschalpreis noetig ist, nicht nur DASS er
                      fehlt. `secItems.length` dynamisch statt der Mockup-Beispielzahl "3". */}
                  {menuPreisFehlt && (
                    <p className="text-beleg-mikro text-secondary dark:text-[#b8ae9e]">
                      Diese Variante nutzt einen <strong>Pauschalpreis {gesamtpreisWortliste!.praepositional}</strong>. Ohne {gesamtpreisWortliste!.preiswort}
                      {" "}fließen {secItems.length === 1 ? "die 1 Position" : `die ${secItems.length} Positionen`} nicht
                      ins Angebot ein — die Einzelpreise sind nur Kalkulationshilfe.
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    {menuPreisFehlt ? (
                      <span className="text-body-sm text-secondary dark:text-[#b8ae9e]">
                        {gesamtpreisWortliste!.preiswort} fehlt — ohne ihn zählt „{label}" mit 0,00 € in der Summe.
                      </span>
                    ) : (
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        {gesamtpreisWortliste!.preiswort}{sektionsModus === "total_event" ? " gesamt" : " pro Person"}
                      </span>
                    )}
                    <label className="flex items-center gap-2">
                      <span className="sr-only">{gesamtpreisWortliste!.preiswort}{sektionsModus === "total_event" ? " gesamt" : " pro Person"}</span>
                      <div className="relative">
                        {/* Design-Komponenten-Migration (P1): bewusst NICHT auf `<Input>` umgestellt
                            — `Input` (ui/components.tsx) ist keine `forwardRef`-Komponente, ein
                            Callback-Ref wie hier (`gesamtpreisInputRefs`, fuer den "Übernehmen"-Knopf
                            unten gebraucht) liefe damit ins Leere (React haengt Refs an
                            Funktionskomponenten ohne `forwardRef` nicht an, `el` bliebe immer
                            `null`). Bleibt darum roh, gleiche Begruendung wie der Ref-Fall in den
                            vorherigen Arbeitspaketen dieses Loops. */}
                        <Input
                          ref={(el) => {
                            gesamtpreisInputRefs.current[key] = el;
                          }}
                          type="text"
                          disabled={!editable}
                          className="!w-28 pr-6"
                          placeholder="z. B. 45,00"
                          defaultValue={sektionsTotalCents != null ? centsToEuros(sektionsTotalCents) : ""}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                          }}
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            const setzen = key === "menu" ? onSetMenuTotalPrice : (cents: number | null) => onSetSectionPricing(key, "total", cents);
                            if (raw === "") return void setzen(null);
                            const cents = parseGermanEurosToCents(raw);
                            if (cents == null) return setError(`Ungültiger ${gesamtpreisWortliste!.preiswort}.`);
                            void setzen(cents);
                          }}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-body-sm text-on-surface-variant">€</span>
                      </div>
                    </label>
                    {/* Mockup 34a: expliziter „Übernehmen"-Knopf — derselbe Commit wie `onBlur`
                        oben (kein zweiter Speicherweg), nur zusaetzlich per Klick statt nur beim
                        Verlassen des Felds. Deckt den Fall, dass jemand tippt und direkt auf
                        einen anderen Knopf klickt, ohne vorher woanders hinzuklicken. */}
                    {menuPreisFehlt && (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={!editable}
                        className="!px-3 !py-1.5 text-beleg-mikro"
                        onClick={() => {
                          const raw = gesamtpreisInputRefs.current[key]?.value.trim() ?? "";
                          if (raw === "") return;
                          const cents = parseGermanEurosToCents(raw);
                          if (cents == null) return setError(`Ungültiger ${gesamtpreisWortliste!.preiswort}.`);
                          // sql/445: `sektionsModus` statt fest "total" — sonst stellt das
                          // Eintragen des Betrags den soeben gewaehlten Modus „Pauschale gesamt"
                          // wieder auf „je Gast" zurueck, und aus 6.500 € werden 78.000 €.
                          void (key === "menu" ? onSetMenuTotalPrice(cents) : onSetSectionPricing(key, sektionsModus, cents));
                        }}
                      >
                        Übernehmen
                      </Button>
                    )}
                    {/* R34-1: Ein-Klick-Vorschlag aus der Positions-Summe (s.o., derselbe
                        lineGross/toEngineItems-Rechenweg) — nur sichtbar, wenn er tatsaechlich
                        einen Vorschlag liefert (Gaestezahl bekannt, Summe > 0). */}
                    {menuPreisFehlt && ausPositionenCents != null && ausPositionenCents > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={!editable}
                        className="!px-2 !py-1 text-beleg-mikro text-primary"
                        onClick={() => void (key === "menu" ? onSetMenuTotalPrice(ausPositionenCents) : onSetSectionPricing(key, sektionsModus, ausPositionenCents))}
                      >
                        Aus Positionen: {money(ausPositionenCents)}
                      </Button>
                    )}
                    {/* Der Kalkulationsabstand („Einzelpreise X/P — Pauschale liegt Y darüber")
                        samt „Pauschale angleichen" stand hier bis zum 17.09.2026.

                        Antoine, nach den Rückmeldungen des Betreibers: „Das ist ganz normal, dass
                        man Menüpreise macht und das gibt fünf Gänge und das Ganze kostet X Euro,
                        da muss man auf nichts hinweisen." Genau dieser Hinweis stand im Screenshot
                        des Betreibers — „Einzelpreise 541,67 €/P — Pauschale liegt 5.958,33 €
                        darüber" — über einer völlig korrekt kalkulierten Raumpauschale. Er
                        verglich zwei Zahlen, die nichts miteinander zu tun haben: eine Pauschale
                        für die Location gegen die Summe von Gängen, die in ihr enthalten sind.

                        „Aus Positionen" darüber BLEIBT: das ist ein Vorschlag im leeren Zustand,
                        keine Bewertung einer bereits getroffenen Entscheidung. */}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Punkt 75, Nachtrag 20.09.2026 (Antoine, mit Bildschirmfoto): „hier sind ueberall
              doppelte Linien drin". Hier stand `divide-y divide-outline-variant/20` — eine
              GRAUE Trennlinie zwischen den Kindern. Jede Zeile zieht aber schon ihre EIGENE
              Linie in der warmen Entwurfsfarbe (`ItemRow`: `border-b border-[#F6F0E4]`, ebenso
              Kopfzeile, Gangzeile und Positionszeile aus `E41A`). Zwei Quellen, zwei Linien,
              sogar in zwei verschiedenen Farben. DIE ZEILE BEHAELT IHRE, der Container gibt
              seine auf: die Linie gehoert zu dem, was sie abschliesst. */}
          <div className={`${isOpenMobile ? "" : "hidden"} lg:block overflow-x-auto`}>
            {/*
              Punkt 75 (Entwurf 41a, Befund ③ „Menge / Einzelpreis / Summe stehen nicht
              untereinander — Vergleichen unmöglich"): eine Kopfzeile, die die Spalten benennt.
              Sie steht nur, wo sie auch etwas ordnet — also ab der Tablet-Breite, wo die Zahlen
              nebeneinander liegen. Auf schmalem Schirm bricht die Zeile ohnehin um (ItemRow:242),
              dort wäre eine Spaltenüberschrift eine Überschrift über nichts.

              `aria-hidden`: Für Screenreader trägt jede Zeile ihre Angaben schon im Text
              („12× · à 14,90 € · 178,80 €"). Eine Kopfzeile ohne echte Tabellenstruktur
              darunter würde dort eine Ordnung behaupten, die es im Baum nicht gibt.
            */}
            {/* Entwurf 41a, Schritt 3: dasselbe Raster wie jede Zeile darunter — erst dadurch
                stehen die Spaltennamen WIRKLICH über ihren Spalten. Vorher war es eine eigene
                Aufteilung, die nur ungefähr passte. */}
            {/* Entwurf 43: dasselbe Raster wie jede Zeile darunter — und zwar buchstäblich
                dasselbe, aus `builder-raster.ts`. Bis hierher hatte die Kopfzeile ein eigenes
                und stand dadurch 16 px neben ihren Spalten. */}
            {/* Mockup v3: die Spaltenköpfe stehen nur, wo eine Zeile auch Zahlen zeigt — steckt
                alles im Paket oder in der Pauschale, wären es Überschriften über leeren Spalten. */}
            {listItems.some((it) => !imPaketIds.has(it.id) && !(istPauschal && it.isOptional !== true && it.surcharge !== true)) && (
            <div aria-hidden className={`hidden sm:grid ${RASTER_43_SPALTEN} items-center gap-x-[12px] ${E41A.tabellenKopf}`}>
              <span />
              <span>Position</span>
              <span>Kennzeichen</span>
              <span className="text-right">Menge</span>
              <span className="text-right">Einzelpreis</span>
              <span className="text-right">Summe</span>
              <span />
            </div>
            )}
            <SortableContext items={secIds} strategy={verticalListSortingStrategy}>
            {paketKoepfe.map((kopf) => renderItemRow(kopf, false, undefined, { enthalten: enthaltenSatz(kopf), aufteilung: paketAufteilung?.get(kopf.id) }))}
            {((rest) =>
              rest && (
                /* Migration 725: grau, nie rot — eine Teilgruppe ohne Paket ist ein Hinweis, kein Fehler. */
                <p className="flex items-start gap-1.5 px-[18px] py-2 font-body-sm text-[12.5px] text-[#6F675A] dark:text-[#b8ae9e]" data-testid="gaeste-ohne-paket">
                  <Icon name="info" className="mt-px shrink-0 text-[15px]" /> {rest.offen} von {rest.alle} Gästen noch ohne Paket.
                </p>
              ))(gaesteOhnePaket(paketKoepfe, guestsForItem))}
            {((gangBloecke) =>
              gangBloecke.map((gang, gangIndex) => {
              /*
                Punkt 60: zwischen je zwei Positionen steht der Trenner — „und" oder „oder", und
                anklickbar. Er ERSETZT den früheren Rahmen um eine Wahlgruppe (Migration 384).

                Der Rahmen sagte dasselbe („der Gast wählt eine dieser Positionen"), ließ sich aber
                nur auflösen, nie anlegen: es gab keinen Weg, eine Wahlgruppe von Hand zu bilden.
                Genau darüber ist Antoine am 19.09.2026 gestolpert. Zwei Anzeigen für dieselbe
                Aussage wären eine zu viel — der Trenner kann beides und ist dort, wo die Frage
                entsteht.

                `secIds`/`SortableContext` bleiben unverändert flach — dnd-kit trackt weiterhin
                jede Zeile einzeln, die Trenner sind reine Anzeige zwischen ihnen.
              */
              /*
                ENTWURF 43: Alternativen einer Wahlgruppe liegen in einer EINGERÜCKTEN KARTE.
                Dafür werden die Positionen eines Gangs zuerst in Blöcke zerlegt: aufeinander
                folgende Zeilen mit derselben Wahlgruppen-Kennung bilden einen Block, alles
                andere bleibt wie bisher eine ununterbrochene Folge.
                
                Warum eine Karte, wo es doch schon die „oder"-Marken gibt: die Marke sagt, dass
                zwischen ZWEI Zeilen gewählt wird. Bei drei Alternativen muss man zwei Marken
                lesen und daraus schließen, dass alle drei zusammengehören. Die Karte zeigt es
                in einem Blick — und die Marken bleiben, wo sie sind, nämlich in der Karte auf
                der Linie zwischen den Alternativen.

                An der Bedienung ändert sich nichts: dieselben Zeilen, dieselben Trenner,
                dasselbe `SortableContext` darüber. Die Karte ist ein Rahmen, kein Behälter.
              */
              const bloecke: { gruppe: string | null; items: OfferItem[] }[] = [];
              for (const it of gang.items) {
                const gruppe = it.choiceGroupId ?? null;
                const letzter = bloecke[bloecke.length - 1];
                if (letzter && letzter.gruppe === gruppe) letzter.items.push(it);
                else bloecke.push({ gruppe, items: [it] });
              }

              /** Der Trenner ÜBER einer Zeile — „und"/„oder" zur Zeile davor. */
              const trennerVor = (it: OfferItem) => {
                const davor = vorherigeZeile(listItems, it);
                // Die erste Zeile der Sektion hat nichts über sich; der Trenner vor dem ersten
                // Gang-Eintrag steht weiter unten beim Gang selbst, sonst stünde er UNTER der
                // Überschrift statt darüber.
                if (davor === null || gang.items[0]!.id === it.id) return null;
                return (
                  <VerbindungsSchalter
                    oder={verbindungOder(it, davor)}
                    editable={editable}
                    onUmschalten={() => {
                      for (const a of verbindungsAenderung(it, davor, uuid)) patchItem(a.id, { choiceGroupId: a.choiceGroupId });
                    }}
                  />
                );
              };

              const zeilen = bloecke.map((block, blockIndex) => {
                const inhalt = block.items.map((it, i) => (
                  /* Fragment statt eines Wrapper-Elements: jede Zeile zieht ihre Trennlinie
                     selbst (`border-b`) — ein zusätzliches Element dazwischen würde einen
                     eigenen Kasten um Trenner und Zeile legen und die Linien verschieben. */
                  <Fragment key={it.id}>
                    {/* In einer Wahlgruppe steht der Trenner zur VORIGEN Position außerhalb der
                        Karte (siehe unten) — innen nur die Marken ZWISCHEN den Alternativen. */}
                    {(block.gruppe == null || i > 0) && trennerVor(it)}
                    {renderItemRow(
                      it,
                      block.gruppe != null,
                      /* „Neuer Gang ab hier": diese und die folgenden Positionen des Gangs
                         bekommen eine eigene Überschrift (patcht dieselben Zeilen wie jede
                         andere Überschrift, Punkt 52). Nicht an der ersten Position. */
                      key === "menu" && gang.items[0]!.id !== it.id
                        ? () => {
                            for (const x of gang.items.slice(gang.items.findIndex((y) => y.id === it.id))) patchItem(x.id, { courseLabel: "Neuer Gang" });
                          }
                        : undefined,
                    )}
                  </Fragment>
                ));
                if (block.gruppe == null) return <Fragment key={`block-${blockIndex}`}>{inhalt}</Fragment>;
                return (
                  <Fragment key={block.gruppe}>
                    {/* Dieser Trenner verbindet die Gruppe mit der Zeile DAVOR und gehört
                        deshalb vor die Karte — innerhalb säße er hinter deren Rahmen. */}
                    {trennerVor(block.items[0]!)}
                    <div className={E43.wahlKarte} data-testid={`builder-wahlkarte-${block.gruppe}`}>
                      {inhalt}
                    </div>
                  </Fragment>
                );
              });
              /* Punkt 52: Gang-Ueberschriften gibt es nur im Menue. In Getraenken, Equipment,
                 Personal und Sonstiges gibt es keine Gaenge — dort waere ueber jeder Zeile ein
                 Knopf, der nie gedrueckt wird. `gruppiereGaenge` laeuft trotzdem (eine
                 bestehende `courseLabel` aus einem Paket wuerde sonst unsichtbar verschwinden),
                 nur die Bedienung entfaellt. */
              if (key !== "menu" && gang.label == null) return zeilen;
              return (
                <div key={gang.schluessel} data-testid={`builder-gang-${gang.schluessel}`}>
                  {/* Punkt 60: der Trenner zwischen zwei Gaengen ist derselbe Schalter wie der
                      zwischen zwei Positionen — „oder" ueber eine Gangrenze hinweg heisst, dass
                      der Gast zwischen den beiden waehlt. */}
                  {gangIndex > 0 && (
                    <VerbindungsSchalter
                      oder={verbindungOder(gang.items[0]!, vorherigeZeile(listItems, gang.items[0]!))}
                      editable={editable}
                      onUmschalten={() => {
                        const erste = gang.items[0]!;
                        for (const a of verbindungsAenderung(erste, vorherigeZeile(listItems, erste), uuid))
                          patchItem(a.id, { choiceGroupId: a.choiceGroupId });
                      }}
                      /* Mockup v3: die Einfügelinie. „+ Position" setzt ans Ende des Gangs
                         DAVOR (mit dessen Überschrift), „+ Gang" beginnt dort einen neuen. */
                      einfuegen={
                        key === "menu" && editable ? (
                          <>
                            <button
                              type="button"
                              className={E43.chipEinfuegen}
                              title={`Position ans Ende von „${gangBloecke[gangIndex - 1]!.label ?? "diesem Gang"}" setzen`}
                              onClick={() => {
                                const vorher = gangBloecke[gangIndex - 1]!;
                                openPositionDrawer(key, { nachId: vorher.items[vorher.items.length - 1]!.id, courseLabel: vorher.label });
                              }}
                            >
                              <Icon name="add" className="text-[13px]" /> Position
                            </button>
                            <button
                              type="button"
                              data-testid="gang-einfuegen"
                              className={E43.chipEinfuegen}
                              title="Hier einen neuen Gang beginnen"
                              onClick={() => {
                                const vorher = gangBloecke[gangIndex - 1]!;
                                openPositionDrawer(key, { nachId: vorher.items[vorher.items.length - 1]!.id, courseLabel: "Neuer Gang" });
                              }}
                            >
                              <Icon name="add" className="text-[13px]" /> Gang
                            </button>
                          </>
                        ) : undefined
                      }
                    />
                  )}
                  <GangUeberschrift
                    label={gang.label}
                    /* Entwurf 43a: gezählt werden nur benannte Gänge — Positionen ohne
                       Überschrift bekommen keine Nummer, weil sie kein Gang sind. */
                    nummer={gang.label ? gangBloecke.slice(0, gangIndex + 1).filter((g) => g.label).length : null}
                    editable={key === "menu" && editable}
                    onSetzen={(wert) => {
                      for (const it of gang.items) patchItem(it.id, { courseLabel: wert });
                    }}
                    anzahl={gang.items.length}
                    /*
                     * Punkt 75: die Zwischensumme des Gangs — dieselbe Rechnung wie die
                     * Abschnittssumme (`toEngineItems` + `lineGross` je Gästezahl der Zeile),
                     * nur auf die Positionen dieses Gangs eingeschränkt. Bewusst dieselbe
                     * Rechenstelle: zwei Wege zur selben Zahl wären zwei Gelegenheiten,
                     * auseinanderzulaufen.
                     */
                    summeCents={gang.items.every((it) => imPaketIds.has(it.id)) ? null : toEngineItems(gang.items).reduce(
                      // Befund 14.10.2026: zubuchbare Positionen zaehlen nicht in die Summe — hier auch nicht.
                      (sum, engineItem, idx) => (gang.items[idx]!.isOptional ? sum : sum + lineGross(engineItem, guestsForItem(gang.items[idx]!))),
                      0,
                    )}
                    nurZubuchbar={gang.items.every((it) => it.isOptional)}
                    /*
                     * Ein Wahlgang ist er nur, wenn ALLE seine Positionen dieselbe Wahlgruppe
                     * tragen — und es mindestens zwei sind. Eine einzelne Position ist keine
                     * Wahl, und zwei Positionen aus verschiedenen Gruppen sind es auch nicht.
                     */
                    istWahlgang={
                      gang.items.length > 1 &&
                      gang.items[0]!.choiceGroupId != null &&
                      gang.items.every((it) => it.choiceGroupId === gang.items[0]!.choiceGroupId)
                    }
                  />
                  {/* Dieselbe doppelte Linie wie oben, hier innerhalb eines Gangs — siehe die
                      Begruendung am Listencontainer. */}
                  <div>{zeilen}</div>
                </div>
              );
              }))(gruppiereGaenge(listItems))}
            </SortableContext>
            {/* Mockup v3: „Gang hinzufügen" fest am Ende des Menüs — die neue Position beginnt
                einen eigenen Gang (Überschrift „Neuer Gang", danach frei umbenennbar). */}
            {key === "menu" && editable && listItems.length > 0 && (
              <button
                type="button"
                data-testid="gang-einfuegen"
                className={E43.gangAktion}
                onClick={() => openPositionDrawer(key, { nachId: listItems[listItems.length - 1]!.id, courseLabel: "Neuer Gang" })}
                title="Eine Position als neuen Gang ans Ende setzen"
              >
                <Icon name="add" className="text-[14px]" />
                Gang hinzufügen
              </button>
            )}
          </div>
          <AbschnittsFuss
            secItems={secItems}
            gaeste={guestCount}
            summeCents={sektionsSumme(key)}
            lmivByCatalogId={lmivByCatalogId}
            mitAllergenen={key === "menu" || key === "drinks"}
            testId={`builder-abschnitts-fuss-${key}`}
          />
        </Card>
      );
    })}
    {/* Mockup v3: „Weitere Positionen – bisher keine" — EINE Zeile für alle leeren Abschnitte,
        je Abschnitt ein „+"-Chip. Derselbe Weg wie bisher der Knopf je leerer Karte
        (`openPositionDrawer`), dasselbe `aria-label`. */}
    {((leer) =>
      leer.length === 0 || !editable ? null : (
        <Card className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-[18px] py-3 ${E41A.karte}`} data-testid="builder-weitere-positionen">
          <span className="text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
            {leer.length === SECTIONS.length ? "Noch keine Positionen" : "Weitere Positionen – bisher keine"}
          </span>
          <span className="flex flex-wrap gap-1.5">
            {leer.map(({ key, label }) => (
              <Button
                key={key}
                type="button"
                variant="ghost"
                data-testid={`builder-section-leer-${key}`}
                className="!gap-1 !rounded-full !border !border-dashed !border-[#E3DBCB] !px-2.5 !py-1 text-[12px] dark:!border-outline/40"
                aria-label={`Position im Abschnitt ${label} hinzufügen`}
                onClick={() => openPositionDrawer(key)}
              >
                <Icon name="add" className="text-[14px]" /> {label}
              </Button>
            ))}
            {leer.length === SECTIONS.length && onPaketUebernehmen && (
              <Button
                type="button"
                variant="ghost"
                data-testid="builder-leer-paket-uebernehmen"
                className="!gap-1 !rounded-full !border !border-dashed !border-[#E3DBCB] !px-2.5 !py-1 text-[12px] dark:!border-outline/40"
                onClick={onPaketUebernehmen}
              >
                <Icon name="inventory_2" className="text-[14px]" /> Paket übernehmen
              </Button>
            )}
          </span>
        </Card>
      ))(SECTIONS.filter((s) => !visibleItems.some((it) => it.section === s.key)))}
    </DndContext>
  ) : null;
}
